import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Link,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberInput,
    NumberInputField,
    Progress,
    SimpleGrid,
    Spacer,
    Text,
    Textarea,
    Tooltip,
    useDisclosure,
    useToast,
    VStack,
    useColorMode,
    Checkbox,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverArrow,
    PopoverCloseButton,
    Stack,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
    AiOutlineSearch,
    AiOutlineLeft,
    AiOutlineRight,
    AiOutlineDelete,
    AiOutlineEdit,
    AiOutlineCheckCircle,
    AiOutlineCloseCircle,
    AiOutlineUndo,
    AiOutlineUser,
    AiOutlineBank,
    AiOutlineCalendar,
    AiOutlinePlus,
    AiOutlinePartition,
    AiOutlineEllipsis,
    AiOutlineClose,
    AiOutlineCheck,
    AiOutlinePhone,
    AiOutlineMail,
    AiOutlineIdcard,
    AiOutlineFire,
    AiOutlineUserAdd,
    AiOutlineDatabase,
    AiOutlineWarning,
    AiOutlineContacts,
    AiOutlineInfoCircle,
    AiOutlineSchedule,
    AiOutlineEye,
    AiOutlineFilter,
    AiOutlineDollarCircle,
    AiOutlineMinus,
} from 'react-icons/ai';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useComercial, CANAIS, PIPELINE_DEFAULT_ID } from '../../../../contexts/ComercialContext';
import { useNogleTech } from '../../../../contexts/NogleTechContext';
import { usePermissions } from '../../../../contexts/AuthPermissionsContext';
import { useAuth } from '../../../../hooks/auth';
import { useModalMinimizer } from '../../../../contexts/ModalMinimizerContext';
import ConfirmDeleteDialog from '../../../../components/UI/ConfirmDeleteDialog';
import ConfirmActionDialog from '../../../../components/UI/ConfirmActionDialog';
import SearchableSelect from '../../../../components/UI/SearchableSelect';
import AnotacaoFormSection from '../../../../components/UI/AnotacaoFormSection';
import { criarAnotacao } from '../../../../services/anotacoes';

// --? Constantes --------------------------------------------------------------

const pulseRed = keyframes`
  0% { transform: scale(0.9); opacity: 0.65; box-shadow: 0 0 0 0 rgba(252, 129, 129, 0.7); }
  50% { transform: scale(1.15); opacity: 1; box-shadow: 0 0 8px 3px rgba(252, 129, 129, 0.55); }
  100% { transform: scale(0.9); opacity: 0.65; box-shadow: 0 0 0 0 rgba(252, 129, 129, 0); }
`;

const pulseYellow = keyframes`
  0% { transform: scale(0.9); opacity: 0.65; box-shadow: 0 0 0 0 rgba(236, 201, 75, 0.7); }
  50% { transform: scale(1.15); opacity: 1; box-shadow: 0 0 8px 3px rgba(236, 201, 75, 0.55); }
  100% { transform: scale(0.9); opacity: 0.65; box-shadow: 0 0 0 0 rgba(236, 201, 75, 0); }
`;

const cardPulseRed = keyframes`
  0% { box-shadow: 0 0 6px rgba(252, 129, 129, 0.15), inset 0 0 3px rgba(252, 129, 129, 0.05); }
  50% { box-shadow: 0 0 12px rgba(252, 129, 129, 0.35), inset 0 0 6px rgba(252, 129, 129, 0.15); }
  100% { box-shadow: 0 0 6px rgba(252, 129, 129, 0.15), inset 0 0 3px rgba(252, 129, 129, 0.05); }
`;

const cardPulseYellow = keyframes`
  0% { box-shadow: 0 0 6px rgba(236, 201, 75, 0.15), inset 0 0 3px rgba(236, 201, 75, 0.05); }
  50% { box-shadow: 0 0 12px rgba(236, 201, 75, 0.35), inset 0 0 6px rgba(236, 201, 75, 0.15); }
  100% { box-shadow: 0 0 6px rgba(236, 201, 75, 0.15), inset 0 0 3px rgba(236, 201, 75, 0.05); }
`;

const PALETA_CORES = [
    '#9F7AEA', '#4299E1', '#ECC94B', '#ED8936', '#FC8181',
    '#02EA75', '#718096', '#F687B3', '#76E4F7', '#FBD38D',
    '#68D391', '#48BB78', '#63B3ED', '#B794F4', '#A0AEC0',
    '#FC8181', '#F6AD55', '#CBD5E0', '#FEB2B2', '#E9D8FD',
];

const PRIORIDADES = [
    { id: 'Alta', cor: '#FC8181' },
    { id: 'Média', cor: '#ECC94B' },
    { id: 'Baixa', cor: '#4299E1' },
];

const CARD_FORM_INICIAL = {
    titulo: '',
    contato: '',
    contatosSelecionados: [],
    empresa: '',
    valor: '',
    probabilidade: 50,
    dataInicio: '',
    dataFechamento: '',
    responsavel: '',
    assignee_id: null,
    canal: '',            // canal é obrigatório no salvar; sem default hardcoded (nome é editável em Controladores)
    prioridade: 'Média',
    observacoes: '',
    marcaId: null,        // id da marca vinda do ERP (NogleTechContext) • null se cliente novo
    clienteNovo: false,   // true quando o cliente NÃO existe em marcas/contatos
    compromissos: [],     // array de eventos vinculados (integra com o Calendário)
    result_id: null,
};

// Tipos de compromisso (agenda)
const TIPOS_COMPROMISSO = [
    { id: 'Reunião', cor: '#4299E1', icon: 'meeting' },
    { id: 'Ligação', cor: '#02EA75', icon: 'phone' },
    { id: 'Follow-up', cor: '#9F7AEA', icon: 'followup' },
    { id: 'Prazo', cor: '#FC8181', icon: 'deadline' },
    { id: 'Entrega', cor: '#ED8936', icon: 'delivery' },
    { id: 'Outro', cor: '#718096', icon: 'other' },
];

const COMPROMISSO_INICIAL = {
    tipo: 'Reunião',
    titulo: '',
    data: '',          // data de início (YYYY-MM-DD)
    dataFim: '',       // data de fim (vazio = mesmo dia)
    horaInicio: '09:00',
    horaFim: '10:00',
    local: '',
    descricao: '',
    concluido: false,
    employee_id: '',
};

const fmt = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

// --? ColorPicker ------------------------------------------------------------?

const ColorPicker = ({ value, onChange }) => (
    <Flex wrap="wrap" gap={2} mt={2}>
        {PALETA_CORES.map(cor => (
            <Box
                key={cor}
                w="22px" h="22px"
                borderRadius="md"
                bg={cor}
                cursor="pointer"
                border="2px solid"
                borderColor={value === cor ? 'white' : 'transparent'}
                boxShadow={value === cor ? `0 0 0 1px ${cor}` : 'none'}
                _hover={{ transform: 'scale(1.25)', boxShadow: `0 0 6px ${cor}80` }}
                transition="all 0.15s"
                onClick={() => onChange(cor)}
                flexShrink={0}
            />
        ))}
    </Flex>
);

// --? EtapaTipoSelector (classifica a etapa: aberto / ganho / perdido) --------?
const ETAPA_TIPOS = [
    { v: 'aberto', l: 'Em aberto', c: '#718096' },
    { v: 'ganho', l: 'Ganho', c: '#01E975' },
    { v: 'perdido', l: 'Perdido', c: '#FC8181' },
];

const EtapaTipoSelector = ({ value, onChange }) => (
    <HStack spacing={2}>
        {ETAPA_TIPOS.map(opt => {
            const active = (value || 'aberto') === opt.v;
            return (
                <Box
                    key={opt.v}
                    as="button"
                    type="button"
                    flex={1}
                    py={2}
                    borderRadius="lg"
                    fontSize="xs"
                    fontWeight="800"
                    border="1px solid"
                    bg={active ? `${opt.c}22` : 'transparent'}
                    color={active ? opt.c : 'textSecondary'}
                    borderColor={active ? opt.c : 'borderPrimary'}
                    transition="all .15s ease"
                    _hover={{ borderColor: opt.c, color: opt.c }}
                    onClick={() => onChange(opt.v)}
                >
                    {opt.l}
                </Box>
            );
        })}
    </HStack>
);

// --? DetailItem (usado no modal de visualização) ----------------------------?

const DetailItem = ({ icon, label, value, valueColor }) => {
    const { colorMode } = useColorMode();
    return (
        <Box bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"} border="1px solid" borderColor="borderPrimary" borderRadius="lg" px={3} py={2.5}>
            <HStack spacing={1.5} mb={0.5}>
                <Icon as={icon} color="textSecondary" boxSize="11px" />
                <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px">
                    {label}
                </Text>
            </HStack>
            <Text fontSize="sm" color={valueColor || 'textPrimary'} fontWeight="700" lineHeight="1.3">
                {value}
            </Text>
        </Box>
    );
};

// --? SecaoLabel --------------------------------------------------------------

const SecaoLabel = ({ label }) => (
    <HStack spacing={3} mt={1}>
        <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="2px" whiteSpace="nowrap">
            {label}
        </Text>
        <Divider borderColor="borderPrimary" />
    </HStack>
);

// Dimensões ajustáveis do board (item: expandir colunas/painel).
// Largura vale para TODAS as colunas; altura null = automática (altura da janela).
const COL_W_MIN = 220;
const COL_W_MAX = 560;
const COL_W_PADRAO = 260;
const BOARD_H_MIN = 340;
const BOARD_H_MAX = 1600;
const COL_ALTURA_AUTO = 'calc(100vh - 240px)';

// --? Pipeline (main) --------------------------------------------------------?

const Pipeline = () => {
    const { colorMode } = useColorMode();
    const {
        pipelines, pipelineCards, contatos, atividades,
        addPipeline, updatePipeline, deletePipeline,
        addColuna, updateColuna, deleteColuna,
        addPipelineCard, updatePipelineCard, deletePipelineCard, moverCard,
        updateCompromisso, deleteCompromisso,
        addAtividade, updateAtividade, resultadosNegocio, fetchResultadosNegocio,
        canaisDropdown, tiposAtividade,
    } = useComercial();
    const navigate = useNavigate();

    // Canais de origem do banco (CONTROLADORES_COMERCIAL_NOGLE type "canais");
    // fallback para a lista fixa CANAIS enquanto o dropdown não carrega.
    const opcoesCanal = useMemo(
        () => (canaisDropdown && canaisDropdown.length > 0 ? canaisDropdown.map(c => c.nome) : CANAIS),
        [canaisDropdown]
    );

    // Marcas vindas da API Cartman (ERP) • usadas no autocomplete da empresa
    const { marcas } = useNogleTech();
    const { employees } = usePermissions();
    const { user } = useAuth();

    const currentEmployee = useMemo(() => {
        if (!user || !employees || employees.length === 0) return null;
        const lowerEmail = user.email?.toLowerCase();
        return employees.find(e => e.email && e.email.toLowerCase() === lowerEmail) || null;
    }, [user, employees]);

    const toast = useToast();

    useEffect(() => {
        if (fetchResultadosNegocio) {
            fetchResultadosNegocio();
        }
    }, [fetchResultadosNegocio]);

    const [atvPage, setAtvPage] = useState(1);
    const atvItemsPerPage = 5;

    // -- Estado local ----------------------------------------------?
    const [pipelineAtivoId, setPipelineAtivoId] = useState(() =>
        localStorage.getItem('comercial_pipeline_ativo') || PIPELINE_DEFAULT_ID
    );
    const [busca, setBusca] = useState('');
    const [buscaContato, setBuscaContato] = useState('');
    const [filterEmployeeIds, setFilterEmployeeIds] = useState([]);
    const [filterSearch, setFilterSearch] = useState('');

    // -- Dimensões do board (largura das colunas / altura do painel) ------
    // Arrastando a borda direita de qualquer coluna → largura (esquerda/direita);
    // arrastando a alça inferior do painel → altura (cima/baixo). Duplo clique
    // restaura o padrão. Persistidas por navegador no localStorage.
    const [colWidth, setColWidth] = useState(() => {
        const v = Number(localStorage.getItem('comercial_pipeline_col_w'));
        return v >= COL_W_MIN && v <= COL_W_MAX ? v : COL_W_PADRAO;
    });
    const [boardHeight, setBoardHeight] = useState(() => {
        const v = Number(localStorage.getItem('comercial_pipeline_board_h'));
        return v >= BOARD_H_MIN && v <= BOARD_H_MAX ? v : null;
    });
    const colWidthRef = useRef(colWidth);
    colWidthRef.current = colWidth;
    const boardHeightRef = useRef(boardHeight);
    boardHeightRef.current = boardHeight;
    const boardScrollRef = useRef(null);

    const iniciarResizeLargura = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startW = colWidthRef.current;
        const onMove = (ev) => {
            setColWidth(Math.round(Math.min(COL_W_MAX, Math.max(COL_W_MIN, startW + (ev.clientX - startX)))));
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            localStorage.setItem('comercial_pipeline_col_w', String(colWidthRef.current));
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    }, []);

    const resetLargura = useCallback(() => {
        setColWidth(COL_W_PADRAO);
        localStorage.removeItem('comercial_pipeline_col_w');
    }, []);

    const iniciarResizeAltura = useCallback((e) => {
        e.preventDefault();
        const startY = e.clientY;
        const alturaInicial = boardHeightRef.current
            ?? Math.max(BOARD_H_MIN, Math.round((boardScrollRef.current?.getBoundingClientRect().height || 640) - 32));
        const onMove = (ev) => {
            setBoardHeight(Math.round(Math.min(BOARD_H_MAX, Math.max(BOARD_H_MIN, alturaInicial + (ev.clientY - startY)))));
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            if (boardHeightRef.current != null) {
                localStorage.setItem('comercial_pipeline_board_h', String(boardHeightRef.current));
            }
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    }, []);

    const resetAltura = useCallback(() => {
        setBoardHeight(null);
        localStorage.removeItem('comercial_pipeline_board_h');
    }, []);

    const pipelineAtivo = pipelines.find(p => p.id === pipelineAtivoId) || pipelines[0];
    const pipelineAtivoOwner = useMemo(() => {
        if (!pipelineAtivo?.assignee_id) return null;
        return (employees || []).find(emp => String(emp.id) === String(pipelineAtivo.assignee_id)) || null;
    }, [employees, pipelineAtivo]);

    const handleSetPipelineAtivo = useCallback((id) => {
        setPipelineAtivoId(id);
        localStorage.setItem('comercial_pipeline_ativo', id);
    }, []);

    // -- Cards do pipeline ativo ------------------------------------
    const cardsDoBoard = useMemo(() => {
        if (!pipelineAtivo) return [];
        const q = busca.toLowerCase();
        return pipelineCards.filter(c => {
            if (c.pipelineId !== pipelineAtivo.id) return false;
            // Concluído (qualquer resultado: Ganho, Perdido, Em espera...) SAI do
            // board — passa a viver em Relatórios. Cancelado continua visível.
            if (c.completed) return false;
            if (filterEmployeeIds.length > 0 && !filterEmployeeIds.includes(String(c.assignee_id))) return false;
            if (!q) return true;
            return (
                c.titulo?.toLowerCase().includes(q) ||
                c.contato?.toLowerCase().includes(q) ||
                c.empresa?.toLowerCase().includes(q)
            );
        });
    }, [pipelineCards, pipelineAtivo, busca, filterEmployeeIds]);

    const cardsPorColuna = useMemo(() => {
        const result = {};
        (pipelineAtivo?.colunas || []).forEach(col => { result[col.id] = []; });
        cardsDoBoard.forEach(c => {
            if (result[c.colunaId] !== undefined) result[c.colunaId].push(c);
        });
        return result;
    }, [cardsDoBoard, pipelineAtivo]);

    const totalPipeline = useMemo(() =>
        cardsDoBoard.filter(c => !c.canceled).reduce((acc, c) => acc + (Number(c.valor) || 0), 0)
        , [cardsDoBoard]);

    // Agrega TODOS os compromissos pendentes de TODOS os cards do pipeline ativo
    const proximosEventos = useMemo(() => {
        if (!pipelineAtivo) return [];
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const eventos = [];
        cardsDoBoard.forEach(card => {
            if (card.canceled) return;
            (card.compromissos || []).forEach(comp => {
                if (comp.concluido || !comp.data) return;
                eventos.push({
                    ...comp,
                    cardId: card.id,
                    cardTitulo: card.titulo,
                    cardEmpresa: card.empresa,
                    cardColunaId: card.colunaId,
                    dataObj: new Date(comp.data + 'T' + (comp.horaInicio || comp.hora || '00:00')),
                });
            });
        });
        return eventos
            .sort((a, b) => a.dataObj - b.dataObj)
            .slice(0, 8);
    }, [cardsDoBoard, pipelineAtivo]);

    // -- Modal: Card (criar/editar) --------------------------------
    const { isOpen: isCardOpen, onOpen: onCardOpen, onClose: onCardClose } = useDisclosure();
    const [cardEditando, setCardEditando] = useState(null);
    const [cardColunaId, setCardColunaId] = useState('');
    // Funil selecionado no formulário (dropdown "Funil"): permite mover o negócio
    // de funil — o banco deriva o funil da coluna (colunaId) escolhida ao salvar.
    // Padrão: funil do card (edição) ou o board ativo (criação).
    const [funilFormId, setFunilFormId] = useState(null);
    const funilForm = (pipelines || []).find(p => String(p.id) === String(funilFormId)) || pipelineAtivo;
    const colunasFormulario = [...(funilForm?.colunas || [])].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
    const [formCard, setFormCard] = useState(CARD_FORM_INICIAL);
    const [novaAnotacao, setNovaAnotacao] = useState('');

    // -- Modal: Visualizar Card (read-only) ------------------------
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
    const [cardVisualizando, setCardVisualizando] = useState(null);

    // -- Modal: Confirmar Conclusão (Ganho/Perdido) ----------------
    const { isOpen: isConcluirOpen, onOpen: onConcluirOpen, onClose: onConcluirClose } = useDisclosure();
    const [cardConcluindo, setCardConcluindo] = useState(null);

    // -- AlertDialog: Confirmar exclusão --------------------------?
    const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();
    const [cardExcluindo, setCardExcluindo] = useState(null);

    // -- Confirmar exclusão de coluna e de pipeline (aba) ----------
    const { isOpen: isDelColunaOpen, onOpen: onDelColunaOpen, onClose: onDelColunaClose } = useDisclosure();
    const [colunaExcluindo, setColunaExcluindo] = useState(null);
    const { isOpen: isDelPipelineOpen, onOpen: onDelPipelineOpen, onClose: onDelPipelineClose } = useDisclosure();
    const [pipelineExcluindo, setPipelineExcluindo] = useState(null);
    const pipelineDeleteImpact = useMemo(() => {
        if (!pipelineExcluindo) return { negocios: 0, atividades: 0 };
        const cards = (pipelineCards || []).filter(c => String(c.pipelineId) === String(pipelineExcluindo.id));
        const cardIds = new Set(cards.map(c => String(c.id)));
        const atividadesVinculadas = (atividades || []).filter(a => a.cardId != null && cardIds.has(String(a.cardId)));
        return { negocios: cards.length, atividades: atividadesVinculadas.length };
    }, [pipelineExcluindo, pipelineCards, atividades]);

    // -- AlertDialog: Confirmar criação com empresa nova/externa --?
    const { isOpen: isConfirmEmpresaOpen, onOpen: onConfirmEmpresaOpen, onClose: onConfirmEmpresaClose } = useDisclosure();
    const confirmEmpresaRef = useRef(null);

    // -- Drag & Drop ----------------------------------------------?
    const [draggedCardId, setDraggedCardId] = useState(null);
    const [draggedAtividade, setDraggedAtividade] = useState(null);
    const [atividadeVinculoPendente, setAtividadeVinculoPendente] = useState(null);
    const [dragOverColId, setDragOverColId] = useState(null);
    // Zona lateral ativa ao arrastar um card p/ trocar de funil ('next' | 'prev' | null)
    const [dragOverFunilDir, setDragOverFunilDir] = useState(null);

    const handleDragStart = useCallback((cardId) => {
        setDraggedCardId(cardId);
    }, []);

    const handleDragEnd = useCallback(() => {
        setDraggedCardId(null);
        setDraggedAtividade(null);
        setDragOverColId(null);
        setDragOverFunilDir(null);
    }, []);

    // Funil vizinho na ordem das abas (à direita = próximo; à esquerda = anterior).
    const funilVizinho = useCallback((dir) => {
        const idx = pipelines.findIndex(p => String(p.id) === String(pipelineAtivo?.id));
        if (idx < 0) return null;
        return dir === 'next' ? (pipelines[idx + 1] || null) : (pipelines[idx - 1] || null);
    }, [pipelines, pipelineAtivo]);

    // 1ª etapa aberta do funil de destino (fallback: 1ª por ordem) — para onde o card cai.
    const primeiraColunaDoFunil = useCallback((funil) => {
        const cols = [...(funil?.colunas || [])].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
        return cols.find(c => c.tipo === 'aberto') || cols[0] || null;
    }, []);

    const handleDropFunilVizinho = useCallback((e, dir) => {
        e.preventDefault();
        const cardId = draggedCardId;
        setDragOverFunilDir(null);
        setDraggedCardId(null);
        setDragOverColId(null);
        // Só cards trocam de funil por aqui (arrastar atividade continua criando negócio).
        if (!cardId || draggedAtividade) { setDraggedAtividade(null); return; }
        const alvo = funilVizinho(dir);
        if (!alvo) {
            toast({ title: dir === 'next' ? 'Não há funil à direita' : 'Não há funil à esquerda', description: 'Crie um novo funil na barra de abas para mover o negócio para o lado.', status: 'info', duration: 2500 });
            return;
        }
        const col = primeiraColunaDoFunil(alvo);
        if (!col) {
            toast({ title: `O funil "${alvo.nome}" não tem colunas`, description: 'Adicione uma coluna nesse funil antes de mover o negócio.', status: 'warning', duration: 3000 });
            return;
        }
        moverCard(cardId, col.id);
        toast({ title: `Negócio movido para o funil "${alvo.nome}"`, description: `Etapa: ${col.label}.`, status: 'success', duration: 2500 });
    }, [draggedCardId, draggedAtividade, funilVizinho, primeiraColunaDoFunil, moverCard, toast]);

    const handleDragOverColuna = useCallback((e, colunaId) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = draggedAtividade ? 'copy' : 'move';
        if (dragOverColId !== colunaId) setDragOverColId(colunaId);
    }, [dragOverColId, draggedAtividade]);

    // Soltar uma ATIVIDADE numa coluna • ABRE o modal de novo negócio já pré-preenchido
    // (etapa + empresa + responsável da atividade). O vínculo • feito ao salvar.
    const abrirNovoNegocioDeAtividade = useCallback((atividade, colunaId) => {
        setCardEditando(null);
        setCardColunaId(colunaId);
        setFunilFormId(pipelineAtivo?.id || null);
        setAtividadeVinculoPendente(atividade);
        setNovaAnotacao('');
        setFormCard({
            ...CARD_FORM_INICIAL,
            colunaId,
            titulo: atividade.empresa || atividade.nome || '',
            empresa: atividade.empresa || '',
            marcaId: atividade.company_id || null,
            company_id: atividade.company_id || null,
            responsavel: atividade.responsavel || '',
            assignee_id: atividade.assignee_id || null,
            contatosSelecionados: [],
        });
        setEmpresaDropdownAberto(false);
        onCardOpen();
    }, [onCardOpen, pipelineAtivo]);

    const handleDropColuna = useCallback((e, colunaId) => {
        e.preventDefault();
        if (draggedAtividade) {
            abrirNovoNegocioDeAtividade(draggedAtividade, colunaId);
        } else if (draggedCardId) {
            const card = pipelineCards.find(c => c.id === draggedCardId);
            if (card && card.colunaId !== colunaId) {
                moverCard(draggedCardId, colunaId);
                // Coluna de conclusão (tipo ganho/perdido) marca completed=1 e o
                // card sai do board — avisa para não parecer que ele sumiu.
                const colDestino = (pipelineAtivo?.colunas || []).find(c => String(c.id) === String(colunaId));
                if (colDestino && colDestino.tipo && colDestino.tipo !== 'aberto') {
                    toast({
                        title: 'Negócio movido!',
                        status: 'success',
                        duration: 3000,
                    });
                } else {
                    toast({ title: 'Negócio movido', status: 'success', duration: 1500 });
                }
            }
        }
        setDraggedCardId(null);
        setDraggedAtividade(null);
        setDragOverColId(null);
    }, [draggedAtividade, abrirNovoNegocioDeAtividade, draggedCardId, pipelineCards, moverCard, pipelineAtivo, toast]);

    // Busca de empresa com autocomplete (Marcas ERP + Contatos + fallback)
    const [empresaDropdownAberto, setEmpresaDropdownAberto] = useState(false);
    const empresaInputRef = useRef(null);

    // Marcas filtradas (ERP • fonte primária)
    const marcasFiltradas = useMemo(() => {
        const q = formCard.empresa?.toLowerCase() || '';
        if (q.length < 1) return [];
        return (marcas || [])
            .filter(m => m.nome?.toLowerCase().includes(q))
            .slice(0, 5);
    }, [marcas, formCard.empresa]);

    // Contatos filtrados (CRM local • fonte secundária; exclui empresas já listadas em marcas)
    const contatosFiltrados = useMemo(() => {
        const q = formCard.empresa?.toLowerCase() || '';
        if (q.length < 1) return [];
        const nomesMarcas = new Set(marcasFiltradas.map(m => m.nome?.toLowerCase()));
        const seen = new Set();
        return contatos
            .filter(c => {
                const empresaLower = c.empresa?.toLowerCase();
                if (!empresaLower || nomesMarcas.has(empresaLower)) return false;
                if (seen.has(empresaLower)) return false;
                seen.add(empresaLower);
                return (
                    c.empresa?.toLowerCase().includes(q) ||
                    c.nome?.toLowerCase().includes(q)
                );
            })
            .slice(0, 4);
    }, [contatos, formCard.empresa, marcasFiltradas]);

    const temResultados = marcasFiltradas.length > 0 || contatosFiltrados.length > 0;

    const [contatoFocado, setContatoFocado] = useState(false);
    const [responsavelFocado, setResponsavelFocado] = useState(false);

    const contatosSugeridos = useMemo(() => {
        const query = buscaContato?.trim().toLowerCase();
        if (!query) return [];
        const queryDigits = query.replace(/\D/g, '');
        const hasExactMatch = contatos.some(c =>
            c.nome?.toLowerCase() === query ||
            (queryDigits && c.telefone?.replace(/\D/g, '') === queryDigits)
        );
        if (hasExactMatch) return [];
        return contatos.filter(c => {
            const nameMatch = c.nome?.toLowerCase().includes(query);
            const phoneDigits = c.telefone?.replace(/\D/g, '') || '';
            const phoneMatch = queryDigits && phoneDigits.includes(queryDigits);
            return nameMatch || phoneMatch;
        }).slice(0, 8);
    }, [contatos, buscaContato]);

    const contatoNaoEncontrado = useMemo(() => {
        const query = buscaContato?.trim().toLowerCase();
        if (!query) return false;
        const queryDigits = query.replace(/\D/g, '');
        return !contatos.some(c => {
            const nameMatch = c.nome?.toLowerCase().includes(query);
            const phoneDigits = c.telefone?.replace(/\D/g, '') || '';
            const phoneMatch = queryDigits && phoneDigits.includes(queryDigits);
            return nameMatch || phoneMatch;
        });
    }, [contatos, buscaContato]);

    const responsaveisSugeridos = useMemo(() => {
        const query = formCard.responsavel?.trim().toLowerCase();
        if (!query) return [];
        const list = (employees || []).filter(e => e.isWhitelisted !== false && e.isWhitelisted !== 0);
        if (list.some(e => e.name?.toLowerCase() === query)) return [];
        return list.filter(e => e.name?.toLowerCase().includes(query)).slice(0, 8);
    }, [employees, formCard.responsavel]);

    const handleSelecionarContatoCard = useCallback((c) => {
        const marca = c.empresa ? (marcas || []).find(m => m.nome?.toLowerCase() === c.empresa?.toLowerCase()) : null;
        setFormCard(p => {
            const novosContatos = p.contatosSelecionados && p.contatosSelecionados.includes(c.nome) ? p.contatosSelecionados : [...(p.contatosSelecionados || []), c.nome];
            const podePreencherEmpresa = !p.empresa?.trim() && !p.marcaId;
            return {
                ...p,
                contatosSelecionados: novosContatos,
                contato: novosContatos.join(', '),
                empresa: podePreencherEmpresa && marca ? marca.nome : p.empresa,
                marcaId: podePreencherEmpresa && marca ? marca.id : p.marcaId,
                clienteNovo: podePreencherEmpresa && marca ? false : p.clienteNovo,
            };
        });
        setBuscaContato('');
        setContatoFocado(false);
    }, [marcas]);

    const handleSelecionarResponsavelCard = useCallback((emp) => {
        setFormCard(p => ({
            ...p,
            responsavel: emp.name,
            assignee_id: emp.id
        }));
        setResponsavelFocado(false);
    }, []);

    // Marca vinculada ao card (se houver marcaId)
    const marcaVinculada = useMemo(() => {
        if (!formCard.marcaId) return null;
        return (marcas || []).find(m => m.id === formCard.marcaId) || null;
    }, [marcas, formCard.marcaId]);

    // Contato escolhido no campo Contato. Não usa a empresa para evitar sobrescrever/escolher contato por engano.
    const contatoSelecionado = useMemo(() => {
        const selecionados = formCard.contatosSelecionados || [];
        const nomes = selecionados.length > 0
            ? selecionados
            : String(formCard.contato || '').split(',').map(n => n.trim()).filter(Boolean);
        if (nomes.length === 0) return null;
        return contatos.find(c =>
            nomes.some(nome => c.nome?.toLowerCase() === nome.toLowerCase())
        ) || null;
    }, [contatos, formCard.contato, formCard.contatosSelecionados]);

    const handleSelecionarMarca = useCallback((marca) => {
        setFormCard(p => ({
            ...p,
            empresa: marca.nome,
            contato: p.contato || marca.responsavel_marca_nome || '',
            marcaId: marca.id,
            clienteNovo: false,
        }));
        setEmpresaDropdownAberto(false);
    }, []);

    const handleUsarClienteNovo = useCallback(() => {
        setFormCard(p => ({
            ...p,
            marcaId: null,
            clienteNovo: true,
        }));
        setEmpresaDropdownAberto(false);
    }, []);

    const handleAbrirVisualizar = useCallback((card) => {
        setCardVisualizando(card);
        onViewOpen();
    }, [onViewOpen]);

    const handleFecharVisualizar = useCallback(() => {
        setCardVisualizando(null);
        onViewClose();
    }, [onViewClose]);

    const handleAbrirExcluir = useCallback((card) => {
        setCardExcluindo(card);
        onDelOpen();
    }, [onDelOpen]);

    // Cancelar/Reativar negócio: flag canceled no banco (o card fica na coluna onde
    // está, esmaecido) — funciona em qualquer etapa aberta, sem depender de coluna "Perdido".
    const handleCancelarNegocio = useCallback(async (card) => {
        if (!card) return;
        try {
            await updatePipelineCard(card.id, { canceled: 1 });
            toast({ title: 'Negócio cancelado', status: 'info', duration: 2000 });
        } catch (err) {
            console.error(err);
            toast({ title: 'Erro ao cancelar negócio', status: 'error', duration: 3000 });
        }
    }, [updatePipelineCard, toast]);

    const handleReativarNegocio = useCallback(async (card) => {
        if (!card) return;
        try {
            await updatePipelineCard(card.id, { canceled: 0 });
            toast({ title: 'Negócio reativado', status: 'success', duration: 2000 });
        } catch (err) {
            console.error(err);
            toast({ title: 'Erro ao reativar negócio', status: 'error', duration: 3000 });
        }
    }, [updatePipelineCard, toast]);

    // Confirmação de ações de 1 clique (cancelar/reativar/concluir) — evita cliques acidentais
    const [acaoConfirmar, setAcaoConfirmar] = useState(null); // { tipo, card, fecharFicha }

    const executarAcaoConfirmada = useCallback(async () => {
        if (!acaoConfirmar) return;
        const { tipo, card, fecharFicha } = acaoConfirmar;
        if (tipo === 'concluir') {
            // Card já em coluna Ganho/Perdido: marca completed=1 (vai para Relatórios)
            // e grava também o resultado correspondente do dropdown resultados_negocio.
            // Regra: resultado ativo ⟺ concluído; "Em aberto" ⟺ card no board.
            try {
                // O tipo da coluna (ganho/perdido) define o PAPEL do resultado a gravar
                // (1 positivo · 2 negativo). Acha o resultado por papel; fallback pelo nome.
                const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
                const colCard = (pipelineAtivo?.colunas || []).find(c => String(c.id) === String(card.colunaId));
                const papelAlvo = colCard?.tipo === 'ganho' ? 1 : (colCard?.tipo === 'perdido' ? 2 : null);
                const resObj = papelAlvo
                    ? ((resultadosNegocio || []).find(r => Number(r.role) === papelAlvo)
                        || (resultadosNegocio || []).find(r => norm(r.nome).includes(papelAlvo === 1 ? 'ganho' : 'perd')))
                    : null;
                await updatePipelineCard(card.id, { completed: 1, ...(resObj ? { result_id: resObj.id } : {}) });
                toast({ title: 'Negócio concluído!', description: 'Movido para a tela Relatórios.', status: 'success', duration: 2500 });
                if (fecharFicha) handleFecharVisualizar();
            } catch (err) {
                toast({ title: 'Erro ao concluir negócio', status: 'error', duration: 2500 });
            }
            setAcaoConfirmar(null);
            return;
        }
        if (tipo === 'cancelar') await handleCancelarNegocio(card);
        else await handleReativarNegocio(card);
        if (fecharFicha) handleFecharVisualizar();
        setAcaoConfirmar(null);
    }, [acaoConfirmar, handleCancelarNegocio, handleReativarNegocio, updatePipelineCard, toast, handleFecharVisualizar, pipelineAtivo, resultadosNegocio]);

    // -- Gestáo de Compromissos (Agenda) --------------------------
    const [novoCompromissoAberto, setNovoCompromissoAberto] = useState(false);
    const [novoCompromisso, setNovoCompromisso] = useState(COMPROMISSO_INICIAL);
    const [editandoCompromissoId, setEditandoCompromissoId] = useState(null);

    const handleEditarCompromisso = useCallback((comp) => {
        setNovoCompromisso({
            titulo: comp.titulo || '',
            tipo: comp.tipo || TIPOS_COMPROMISSO[0].id,
            data: comp.data || '',
            dataFim: comp.dataFim || comp.data || '',
            horaInicio: comp.horaInicio || comp.hora || '00:00',
            horaFim: comp.horaFim || '',
            local: comp.local || '',
            descricao: comp.descricao || '',
            concluido: !!comp.concluido
        });
        setEditandoCompromissoId(comp.id);
        setNovoCompromissoAberto(true);
    }, []);

    // Mapeia um item da agenda do negócio • payload de ATIVIDADE (evento de calendário).
    // Responsável do negócio • o padrão; vincula ao negócio via cardId/ligarPipeline.
    const compromissoParaAtividade = useCallback((comp, cardId) => ({
        nome: comp.titulo,
        dataInicio: comp.data,
        horaInicio: comp.horaInicio || '00:00',
        dataFim: comp.dataFim || comp.data,
        horaFim: comp.horaFim || comp.horaInicio || '00:00',
        tipo: comp.tipo,
        status: 'pendente',
        assignee_id: comp.employee_id || formCard.assignee_id || null,
        company_id: formCard.company_id || formCard.marcaId || null,
        observacao: [comp.local, comp.descricao].filter(Boolean).join(' • '),
        ligarPipeline: true,
        cardId: cardId != null ? cardId : null,
    }), [formCard.assignee_id, formCard.company_id, formCard.marcaId]);

    const handleAdicionarCompromisso = useCallback(async () => {
        if (!novoCompromisso.titulo?.trim() || !novoCompromisso.data) {
            toast({ title: 'Título e data são obrigatórios', status: 'warning', duration: 2000 });
            return;
        }
        const dataFim = novoCompromisso.dataFim || novoCompromisso.data;
        if (dataFim < novoCompromisso.data) {
            toast({ title: 'A data de fim não pode ser anterior ao início', status: 'warning', duration: 2500 });
            return;
        }
        const hojeStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local
        if (novoCompromisso.data < hojeStr) {
            toast({ title: 'Não é possível agendar em uma data passada', status: 'warning', duration: 2500 });
            return;
        }
        if (dataFim === novoCompromisso.data && novoCompromisso.horaInicio && novoCompromisso.horaFim
            && novoCompromisso.horaFim < novoCompromisso.horaInicio) {
            toast({ title: 'O horário de fim deve ser após o início', status: 'warning', duration: 2500 });
            return;
        }

        const compData = { ...novoCompromisso, dataFim };

        if (editandoCompromissoId) {
            const isTempId = String(editandoCompromissoId).startsWith('temp-') || String(editandoCompromissoId).startsWith('ev-');
            if (cardEditando && !isTempId) {
                try {
                    await updateCompromisso(cardEditando.id, editandoCompromissoId, {
                        titulo: compData.titulo,
                        tipo: compData.tipo,
                        data: compData.data,
                        dataFim: compData.dataFim,
                        horaInicio: compData.horaInicio,
                        horaFim: compData.horaFim,
                        local: compData.local,
                        descricao: compData.descricao
                    });
                    setFormCard(p => ({
                        ...p,
                        compromissos: (p.compromissos || []).map(c =>
                            c.id === editandoCompromissoId ? { ...compData, id: editandoCompromissoId } : c
                        )
                    }));
                    toast({ title: 'Compromisso atualizado', status: 'success', duration: 2000 });
                } catch (err) {
                    toast({ title: 'Erro ao atualizar compromisso', status: 'error', duration: 2000 });
                }
            } else {
                setFormCard(p => ({
                    ...p,
                    compromissos: (p.compromissos || []).map(c =>
                        c.id === editandoCompromissoId ? { ...compData, id: editandoCompromissoId } : c
                    )
                }));
            }
            setEditandoCompromissoId(null);
        } else {
            if (cardEditando) {
                try {
                    await addAtividade(compromissoParaAtividade(compData, cardEditando.id));
                    setFormCard(p => ({
                        ...p,
                        compromissos: [
                            ...(p.compromissos || []),
                            { ...compData, id: `ev-${Date.now()}` }
                        ]
                    }));
                    toast({ title: 'Evento criado no calendário', status: 'success', duration: 2000 });
                } catch (err) {
                    toast({ title: 'Erro ao agendar compromisso', status: 'error', duration: 2000 });
                }
            } else {
                const tempId = `temp-${Date.now()}`;
                setFormCard(p => ({
                    ...p,
                    compromissos: [
                        ...(p.compromissos || []),
                        { ...compData, id: tempId }
                    ]
                }));
            }
        }

        setNovoCompromisso(COMPROMISSO_INICIAL);
        setNovoCompromissoAberto(false);
    }, [novoCompromisso, cardEditando, editandoCompromissoId, addAtividade, updateCompromisso, compromissoParaAtividade, toast]);

    const handleRemoverCompromisso = useCallback(async (compId) => {
        if (cardEditando) {
            try {
                await deleteCompromisso(compId);
                setFormCard(p => ({
                    ...p,
                    compromissos: (p.compromissos || []).filter(c => c.id !== compId),
                }));
                toast({ title: 'Compromisso removido', status: 'info', duration: 2000 });
            } catch (err) {
                toast({ title: 'Erro ao remover compromisso', status: 'error', duration: 2000 });
            }
        } else {
            setFormCard(p => ({
                ...p,
                compromissos: (p.compromissos || []).filter(c => c.id !== compId),
            }));
        }
    }, [cardEditando, deleteCompromisso, toast]);

    const handleToggleCompromissoConcluido = useCallback(async (compId) => {
        const target = (formCard.compromissos || []).find(c => c.id === compId);
        if (!target) return;

        const novoEstadoConcluido = !target.concluido;

        if (cardEditando) {
            try {
                await updateCompromisso(cardEditando.id, compId, { concluido: novoEstadoConcluido });
                setFormCard(p => ({
                    ...p,
                    compromissos: (p.compromissos || []).map(c =>
                        c.id === compId ? { ...c, concluido: novoEstadoConcluido } : c
                    ),
                }));
            } catch (err) {
                toast({ title: 'Erro ao atualizar status', status: 'error', duration: 2000 });
            }
        } else {
            setFormCard(p => ({
                ...p,
                compromissos: (p.compromissos || []).map(c =>
                    c.id === compId ? { ...c, concluido: novoEstadoConcluido } : c
                ),
            }));
        }
    }, [cardEditando, formCard, updateCompromisso, toast]);

    const handleConfirmarConclusao = useCallback(async (status, resultId, resultRole) => {
        if (!cardConcluindo) return;
        const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
        const alvo = norm(status);
        const colunas = pipelineAtivo?.colunas || [];

        // Coluna de destino é OPCIONAL e escolhida pelo PAPEL do resultado
        // (1 positivo → coluna ganho · 2 negativo → coluna perdido). Fallback pelo
        // nome (igual/contém/prefixo — cobre "Perda" x "Perdido") para bancos sem
        // papel. Resultado neutro sem coluna correspondente conclui onde está.
        let colTarget = null;
        if (resultRole === 1) colTarget = colunas.find(col => col.tipo === 'ganho');
        else if (resultRole === 2) colTarget = colunas.find(col => col.tipo === 'perdido');
        else if (resultRole == null && alvo.includes('ganho')) colTarget = colunas.find(col => col.tipo === 'ganho');
        else if (resultRole == null && alvo.includes('perd')) colTarget = colunas.find(col => col.tipo === 'perdido');
        if (!colTarget && resultRole !== 0) {
            colTarget = colunas.find(col => {
                const label = norm(col.label || col.nome);
                if (!label) return false;
                return label === alvo
                    || label.includes(alvo)
                    || alvo.includes(label)
                    || (alvo.length >= 4 && label.slice(0, 4) === alvo.slice(0, 4));
            });
        }

        try {
            // Concluir SEMPRE marca completed=1 → o card sai do board do Pipeline
            // e passa a viver em Relatórios, qualquer que seja o resultado.
            const patch = { completed: 1 };
            if (resultId) patch.result_id = resultId;
            if (colTarget && String(colTarget.id) !== String(cardConcluindo.colunaId)) {
                patch.colunaId = colTarget.id;
            }
            await updatePipelineCard(cardConcluindo.id, patch);
            toast({
                title: `Negócio concluído como ${String(status).toUpperCase()}!`,
                description: 'Negócio movido para Relatórios.',
                status: resultRole === 1 ? 'success' : 'info',
                duration: 3000
            });

            // Fecha as duas modals
            onConcluirClose();
            setCardConcluindo(null);
            handleFecharVisualizar();
        } catch (err) {
            toast({ title: 'Erro ao concluir negócio', status: 'error', duration: 2500 });
        }
    }, [cardConcluindo, pipelineAtivo, updatePipelineCard, onConcluirClose, handleFecharVisualizar, toast]);

    const handleConfirmarExcluir = useCallback(() => {
        if (cardExcluindo) {
            deletePipelineCard(cardExcluindo.id);
            toast({ title: 'Negócio excluído', status: 'info', duration: 2000 });
            // Se o modal de visualização estava aberto para este card, fecha
            if (cardVisualizando?.id === cardExcluindo.id) {
                handleFecharVisualizar();
            }
        }
        setCardExcluindo(null);
        onDelClose();
    }, [cardExcluindo, deletePipelineCard, cardVisualizando, handleFecharVisualizar, onDelClose, toast]);

    const handleConfirmarExcluirColuna = useCallback(() => {
        if (colunaExcluindo) {
            deleteColuna(pipelineAtivo.id, colunaExcluindo.id);
            toast({ title: 'Coluna removida', status: 'info', duration: 2000 });
        }
        setColunaExcluindo(null);
        onDelColunaClose();
    }, [colunaExcluindo, deleteColuna, pipelineAtivo, onDelColunaClose, toast]);

    const handleConfirmarExcluirPipeline = useCallback(() => {
        if (pipelineExcluindo) {
            const outros = pipelines.filter(x => x.id !== pipelineExcluindo.id);
            if (pipelineExcluindo.id === pipelineAtivo?.id && outros.length > 0) {
                handleSetPipelineAtivo(outros[0].id);
            }
            deletePipeline(pipelineExcluindo.id);
            toast({ title: `Pipeline "${pipelineExcluindo.nome}" excluído`, status: 'info', duration: 2000 });
        }
        setPipelineExcluindo(null);
        onDelPipelineClose();
    }, [pipelineExcluindo, pipelines, pipelineAtivo, handleSetPipelineAtivo, deletePipeline, onDelPipelineClose, toast]);

    const handleAbrirCard = useCallback((card = null, colunaId = '', presetTitulo = '') => {
        setCardEditando(card);
        setCardColunaId(card ? card.colunaId : colunaId);
        setFunilFormId(card ? (card.pipelineId || pipelineAtivo?.id || null) : (pipelineAtivo?.id || null));
        setNovaAnotacao('');
        // Garante que compromissos sempre exista como array
        setFormCard(card
            ? {
                ...card,
                dataInicio: card.dataInicio || '',
                dataFechamento: card.dataFechamento || '',
                compromissos: card.compromissos || [],
                contatosSelecionados: card.contato ? card.contato.split(',').map(c => c.trim()).filter(c => c) : []
            }
            : {
                ...CARD_FORM_INICIAL,
                colunaId,
                titulo: presetTitulo || '',
                responsavel: '',
                assignee_id: null,
                contatosSelecionados: []
            }
        );
        setEmpresaDropdownAberto(false);
        setNovoCompromissoAberto(false);
        setNovoCompromisso(COMPROMISSO_INICIAL);
        setBuscaContato('');
        onCardOpen();
    }, [onCardOpen, pipelineAtivo]);

    const { minimizeModal, removeMinimizedModal, consumePendingRestore } = useModalMinimizer();

    const handleMinimizarCard = useCallback(() => {
        onCardClose();
        const modalId = 'negocio-modal';
        const title = cardEditando ? 'Editar Negócio' : 'Novo Negócio';
        const subtitle = formCard.titulo || formCard.empresa || 'Rascunho de Negócio';
        minimizeModal({
            id: modalId,
            title,
            subtitle,
            icon: AiOutlinePartition,
            route: '/comercial/pipeline',
            formData: { formCard, cardEditando, cardColunaId, funilFormId, novaAnotacao },
            onExpand: () => {
                onCardOpen();
            },
            onClose: () => {
                setCardEditando(null);
                setFormCard(CARD_FORM_INICIAL);
                setNovaAnotacao('');
                setEmpresaDropdownAberto(false);
                setNovoCompromissoAberto(false);
                setNovoCompromisso(COMPROMISSO_INICIAL);
                setAtividadeVinculoPendente(null);
            }
        });
    }, [onCardClose, onCardOpen, cardEditando, formCard, cardColunaId, funilFormId, novaAnotacao, minimizeModal]);

    useEffect(() => {
        const pendingData = consumePendingRestore('negocio-modal');
        if (pendingData) {
            if (pendingData.formCard) setFormCard(pendingData.formCard);
            if (pendingData.cardEditando !== undefined) setCardEditando(pendingData.cardEditando);
            if (pendingData.cardColunaId !== undefined) setCardColunaId(pendingData.cardColunaId);
            if (pendingData.funilFormId !== undefined) setFunilFormId(pendingData.funilFormId);
            if (pendingData.novaAnotacao !== undefined) setNovaAnotacao(pendingData.novaAnotacao);
            onCardOpen();
        }
    }, [consumePendingRestore, onCardOpen]);

    const handleFecharCard = useCallback(() => {
        setCardEditando(null);
        setFormCard(CARD_FORM_INICIAL);
        setNovaAnotacao('');
        setEmpresaDropdownAberto(false);
        setNovoCompromissoAberto(false);
        setNovoCompromisso(COMPROMISSO_INICIAL);
        setAtividadeVinculoPendente(null);
        removeMinimizedModal('negocio-modal');
        onCardClose();
    }, [onCardClose, removeMinimizedModal]);

    const handleSalvarCard = useCallback(async (skipConfirmEmpresa = false) => {
        if (!formCard.titulo?.trim()) {
            toast({ title: 'Título obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        if (contatoNaoEncontrado) {
            toast({ title: 'O contato precisa estar cadastrado na lista de contatos', status: 'warning', duration: 3000 });
            return;
        }

        let finalAssigneeId = formCard.assignee_id;
        if (!finalAssigneeId && formCard.responsavel) {
            const foundEmp = (employees || []).find(e => e.name?.toLowerCase() === formCard.responsavel.trim().toLowerCase());
            if (foundEmp) {
                finalAssigneeId = foundEmp.id;
            }
        }

        // Link da reunião (Google saiu): pega o "Local ou link" de um compromisso de Reunião
        // e grava na coluna meet_link do card (visível no card e na tela Concluídas).
        const reuniaoLink = (formCard.compromissos || [])
            .find(c => String(c.tipo || '').toLowerCase().includes('reuni') && c.local)?.local;

        // A etapa precisa pertencer ao funil escolhido no dropdown "Funil"; card
        // "sem funil" (coluna órfã) adota a primeira etapa do funil selecionado.
        const colunaPertenceAoFunil = (funilForm?.colunas || []).some(c => String(c.id) === String(cardColunaId));
        const targetColunaId = (colunaPertenceAoFunil ? cardColunaId : null) || funilForm?.colunas?.[0]?.id;
        // Resultado selecionado = card concluído (sai da pipeline).
        // Sem resultado ("Em aberto") = card aberto (volta ao board na etapa escolhida).
        const completed = !!formCard.result_id;

        // -- Campos obrigatórios do Negócio --
        const empresaOk = !!(formCard.company_id || formCard.marcaId || formCard.empresa?.trim());
        const contatoOk = !!(formCard.contato?.trim() || (formCard.contatosSelecionados || []).length > 0);
        if (!empresaOk) { toast({ title: 'Empresa / marca obrigatória', status: 'warning', duration: 2500 }); return; }
        if (!contatoOk) { toast({ title: 'Contato obrigatório', status: 'warning', duration: 2500 }); return; }
        // Contato precisa estar cadastrado na lista (não aceita contato novo/texto livre)
        const namesToCheck = formCard.contatosSelecionados && formCard.contatosSelecionados.length > 0
            ? formCard.contatosSelecionados
            : formCard.contato ? formCard.contato.split(',').map(n => n.trim()) : [];

        const contatosNaoCadastrados = namesToCheck
            .filter(nome => nome && !(contatos || []).some(c => c.nome?.toLowerCase() === String(nome).trim().toLowerCase()));
        if (contatosNaoCadastrados.length > 0) {
            toast({ title: `Contato "${contatosNaoCadastrados[0]}" não está cadastrado`, description: 'Cadastre o contato em Contatos antes de vincular ao negócio.', status: 'warning', duration: 3500 });
            return;
        }
        if (!targetColunaId) { toast({ title: 'Selecione a etapa do funil', status: 'warning', duration: 2500 }); return; }
        if (!formCard.canal?.trim()) { toast({ title: 'Canal de origem obrigatório', status: 'warning', duration: 2500 }); return; }
        if (!formCard.dataInicio) { toast({ title: 'Data de início obrigatória', status: 'warning', duration: 2500 }); return; }
        if (!formCard.dataFechamento) { toast({ title: 'Data de fechamento obrigatória', status: 'warning', duration: 2500 }); return; }
        const hojeStr = new Date().toLocaleDateString('en-CA');
        if (formCard.dataFechamento < formCard.dataInicio) { toast({ title: 'A data de fechamento não pode ser anterior à de início', status: 'warning', duration: 2500 }); return; }
        if (!cardEditando && formCard.dataInicio < hojeStr) { toast({ title: 'A data de início não pode ser no passado', status: 'warning', duration: 2500 }); return; }
        // Responsável precisa estar cadastrado no banco (não pode ser texto livre/novo)
        if (!finalAssigneeId) {
            toast({ title: formCard.responsavel?.trim() ? 'Responsável precisa estar cadastrado no sistema' : 'Responsável obrigatório', status: 'warning', duration: 3000 });
            return;
        }

        // Empresa nova (externa, sem marca do ERP) • confirma antes de criar
        const empresaNova = !formCard.company_id && !formCard.marcaId;
        if (!cardEditando && empresaNova && !skipConfirmEmpresa) {
            onConfirmEmpresaOpen();
            return;
        }

        const payload = {
            ...formCard,
            // A empresa/marca selecionada vive em `marcaId` (id do ERP / FRANQUIA_MARCA_PROPRIA).
            // O banco espera em `company_id` ?sem este mapeamento, id_company salvava NULL.
            company_id: formCard.marcaId || formCard.company_id || null,
            meetLink: reuniaoLink || formCard.meetLink || '',
            assignee_id: finalAssigneeId || null,
            valor: Number(formCard.valor) || 0,
            colunaId: targetColunaId,
            pipelineId: funilForm?.id,
            completed,
        };

        // Observações novas vivem exclusivamente na linha do tempo. Não reenviar
        // a coluna legada evita sobrescrever o histórico somente leitura.
        delete payload.observacoes;

        let cardSalvoId;
        if (cardEditando) {
            await updatePipelineCard(cardEditando.id, payload);
            cardSalvoId = cardEditando.id;
        } else {
            const newCard = await addPipelineCard(payload);
            cardSalvoId = newCard?.id;
            if (formCard.compromissos && formCard.compromissos.length > 0) {
                for (const comp of formCard.compromissos) {
                    try {
                        await addAtividade(compromissoParaAtividade(comp, newCard.id));
                    } catch (err) {
                        console.error("Erro ao salvar compromisso temporário:", err);
                    }
                }
            }
            // Veio do arraste de uma atividade • vincula ela ao negócio recém-criado.
            if (atividadeVinculoPendente && newCard?.id) {
                try {
                    await updateAtividade(atividadeVinculoPendente.id, { ...atividadeVinculoPendente, cardId: String(newCard.id) });
                } catch (err) {
                    console.error("Erro ao vincular atividade ao novo negócio:", err);
                }
            }

        }

        let anotacaoFalhou = false;
        if (novaAnotacao.trim()) {
            try {
                await criarAnotacao({ refType: 'card', refId: cardSalvoId, text: novaAnotacao });
            } catch (err) {
                anotacaoFalhou = true;
                console.error('Negócio salvo, mas a anotação não foi registrada:', err);
            }
        }

        // Mudança de funil sai deste board — avisa para o card não parecer sumido.
        const mudouFunil = cardEditando
            ? String(cardEditando.pipelineId ?? '') !== String(funilForm?.id ?? '')
            : String(funilForm?.id ?? '') !== String(pipelineAtivo?.id ?? '');
        const partesDescricao = [];
        if (mudouFunil && funilForm?.nome) {
            partesDescricao.push(`Negócio ${cardEditando ? 'movido para' : 'criado em'} outro funil: "${funilForm.nome}".`);
        }
        if (novaAnotacao.trim()) {
            partesDescricao.push(anotacaoFalhou
                ? 'O negócio foi salvo, mas não foi possível registrar a nova observação.'
                : 'Nova observação adicionada à linha do tempo em Anotações.');
        }
        toast({
            title: cardEditando ? 'Negócio atualizado' : 'Negócio criado',
            description: partesDescricao.length > 0 ? partesDescricao.join(' ') : undefined,
            status: anotacaoFalhou ? 'warning' : 'success',
            duration: anotacaoFalhou ? 4500 : (mudouFunil ? 4000 : 2500),
            isClosable: true,
        });
        setAtividadeVinculoPendente(null);
        handleFecharCard();
    }, [formCard, novaAnotacao, cardColunaId, cardEditando, pipelineAtivo, funilForm, addPipelineCard, updatePipelineCard, addAtividade, compromissoParaAtividade, handleFecharCard, toast, contatoNaoEncontrado, contatos, employees, atividadeVinculoPendente, updateAtividade, onConfirmEmpresaOpen]);

    // -- Modal: Coluna (criar) ------------------------------------?
    const { isOpen: isColunaOpen, onOpen: onColunaOpen, onClose: onColunaClose } = useDisclosure();
    const [formColuna, setFormColuna] = useState({ label: '', cor: '#4299E1', tipo: 'aberto' });

    const handleSalvarColuna = useCallback(() => {
        if (!formColuna.label.trim()) {
            toast({ title: 'Nome obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        addColuna(pipelineAtivo.id, formColuna);
        toast({ title: `Coluna "${formColuna.label}" criada`, status: 'success', duration: 2000 });
        setFormColuna({ label: '', cor: '#4299E1', tipo: 'aberto' });
        onColunaClose();
    }, [formColuna, pipelineAtivo, addColuna, onColunaClose, toast]);

    // -- Modal: Renomear coluna ------------------------------------
    const { isOpen: isRenomearOpen, onOpen: onRenomearOpen, onClose: onRenomearClose } = useDisclosure();
    const [colunaRenomeando, setColunaRenomeando] = useState(null);
    const [formRenomear, setFormRenomear] = useState({ label: '', cor: '' });

    const handleAbrirRenomear = useCallback((coluna) => {
        setColunaRenomeando(coluna);
        setFormRenomear({ label: coluna.label, cor: coluna.cor, tipo: coluna.tipo || 'aberto' });
        onRenomearOpen();
    }, [onRenomearOpen]);

    const handleSalvarRenomear = useCallback(() => {
        if (!formRenomear.label.trim()) return;
        updateColuna(pipelineAtivo.id, colunaRenomeando.id, formRenomear);
        toast({ title: 'Coluna atualizada', status: 'success', duration: 2000 });
        onRenomearClose();
    }, [formRenomear, pipelineAtivo, colunaRenomeando, updateColuna, onRenomearClose, toast]);

    // -- Modal: Nova aba (pipeline) --------------------------------
    const { isOpen: isAbaOpen, onOpen: onAbaOpen, onClose: onAbaClose } = useDisclosure();
    const [formAba, setFormAba] = useState({ nome: '', cor: '#4299E1', responsavelId: '' });

    const handleSalvarAba = useCallback(async () => {
        if (!formAba.nome.trim()) {
            toast({ title: 'Nome obrigatório', status: 'warning', duration: 2000 });
            return;
        }
        const novo = await addPipeline(formAba.nome, formAba.cor, formAba.responsavelId || null);
        if (novo?.id) handleSetPipelineAtivo(novo.id);
        toast({ title: `Pipeline "${formAba.nome}" criado`, status: 'success', duration: 2000 });
        setFormAba({ nome: '', cor: '#4299E1', responsavelId: '' });
        onAbaClose();
    }, [formAba, addPipeline, handleSetPipelineAtivo, onAbaClose, toast]);

    // -- Renomear aba --------------------------------------------?
    const { isOpen: isRenomearAbaOpen, onOpen: onRenomearAbaOpen, onClose: onRenomearAbaClose } = useDisclosure();
    const [abaRenomeando, setAbaRenomeando] = useState(null);
    const [formRenomearAba, setFormRenomearAba] = useState({ nome: '', cor: '', responsavelId: '' });

    const handleAbrirRenomearAba = useCallback((pipeline, e) => {
        e.stopPropagation();
        setAbaRenomeando(pipeline);
        setFormRenomearAba({ nome: pipeline.nome, cor: pipeline.cor, responsavelId: pipeline.assignee_id || '' });
        onRenomearAbaOpen();
    }, [onRenomearAbaOpen]);

    const handleSalvarRenomearAba = useCallback(() => {
        if (!formRenomearAba.nome.trim()) return;
        updatePipeline(abaRenomeando.id, {
            nome: formRenomearAba.nome,
            cor: formRenomearAba.cor,
            assignee_id: formRenomearAba.responsavelId || null,
        });
        toast({ title: 'Pipeline atualizado', status: 'success', duration: 2000 });
        onRenomearAbaClose();
    }, [formRenomearAba, abaRenomeando, updatePipeline, onRenomearAbaClose, toast]);

    // -- Mover card entre colunas ----------------------------------
    const handleMoverCard = useCallback((cardId, direcao) => {
        const card = pipelineCards.find(c => c.id === cardId);
        if (!card || !pipelineAtivo) return;
        const colunas = pipelineAtivo.colunas;
        const idx = colunas.findIndex(c => c.id === card.colunaId);
        const novoIdx = direcao === 'next' ? idx + 1 : idx - 1;
        if (novoIdx < 0 || novoIdx >= colunas.length) return;
        moverCard(cardId, colunas[novoIdx].id);
    }, [pipelineCards, pipelineAtivo, moverCard]);

    if (!pipelineAtivo) return null;

    const colunas = [...pipelineAtivo.colunas].sort((a, b) => a.ordem - b.ordem);

    const corProb = formCard.probabilidade >= 70 ? '#02EA75' : formCard.probabilidade >= 40 ? '#ECC94B' : '#FC8181';

    // Fundo do modal Novo/Editar Negócio segue o token `surface` do tema (mesmo
    // preto/branco dos demais modais, ex.: Nova Atividade) — sem vidro acinzentado.
    const modalGlassBg = colorMode === 'dark' ? '#161719' : '#FFFFFF';
    const modalGlassBorder = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 19, 0, 0.16)';
    const inputGlassBg = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 19, 0, 0.02)';
    const inputGlassFocusBg = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 19, 0, 0.03)';
    const inputGlassBorderHover = colorMode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 19, 0, 0.22)';

    return (
        <VStack align="stretch" spacing={5}>

            {/* -- Header -- */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={4} w="100%">
                <Box flexShrink={0}>
                    <Heading size="lg" color="textPrimary" fontWeight="900">Pipeline de Negócios</Heading>
                    {pipelineAtivoOwner && (
                        <HStack spacing={2} mt={2} color="textSecondary">
                            <Avatar
                                size="xs"
                                name={pipelineAtivoOwner.name}
                                src={pipelineAtivoOwner.avatar || pipelineAtivoOwner.url}
                            />
                            <Text fontSize="xs" fontWeight="800" color="textPrimary" noOfLines={1}>
                                {pipelineAtivoOwner.name}
                            </Text>
                        </HStack>
                    )}
                </Box>
                <HStack spacing={3} flexWrap="wrap" justify="flex-end" flexShrink={1} minW={0}>
                    <InputGroup maxW={{ base: '100%', md: '260px' }} minW="160px" flex="1 1 200px" size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={AiOutlineSearch} color="textSecondary" />
                        </InputLeftElement>
                        <Input
                            placeholder="Buscar negócio, empresa..."
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            bg="surface"
                            border="1px solid"
                            borderColor="borderPrimary"
                            _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                            color="textPrimary"
                            borderRadius="xl"
                        />
                    </InputGroup>
                    <Popover placement="bottom-end" closeOnBlur={true}>
                        <PopoverTrigger>
                            <Button
                                size="sm"
                                variant="outline"
                                borderColor="borderPrimary"
                                color="textPrimary"
                                leftIcon={<Icon as={AiOutlineFilter} boxSize={4} />}
                                _hover={{ bg: 'surfaceHover' }}
                                borderRadius="xl"
                                fontWeight="800"
                                flexShrink={0}
                            >
                                Filtros {filterEmployeeIds.length > 0 && `(${filterEmployeeIds.length})`}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent bg="surface" borderColor="borderPrimary" color="textPrimary" w="320px" borderRadius="xl" _focus={{ boxShadow: 'none' }} zIndex={999}>
                            <PopoverArrow bg="surface" />
                            <PopoverHeader borderColor="borderPrimary" fontWeight="bold" fontSize="sm">
                                Filtrar Funcionários
                            </PopoverHeader>
                            <PopoverCloseButton />
                            <PopoverBody maxH="400px" overflowY="auto" p={0}>
                                <Box p={3} borderBottom="1px solid" borderColor="borderPrimary">
                                    <Input
                                        placeholder="Buscar..."
                                        size="sm"
                                        bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                        border="1px solid"
                                        borderColor="borderPrimary"
                                        value={filterSearch}
                                        onChange={(e) => setFilterSearch(e.target.value)}
                                        mb={2}
                                        borderRadius="md"
                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    />
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="green"
                                        w="100%"
                                        onClick={() => setFilterEmployeeIds([])}
                                        isDisabled={filterEmployeeIds.length === 0}
                                    >
                                        Limpar Filtros
                                    </Button>
                                </Box>
                                <Stack spacing={0}>
                                    {employees
                                        ?.filter(emp => {
                                            if (!filterSearch) return true;
                                            return emp.name?.toLowerCase().includes(filterSearch.toLowerCase());
                                        })
                                        .map((emp) => {
                                            const isChecked = filterEmployeeIds.includes(String(emp.id));
                                            return (
                                                <Box
                                                    key={emp.id}
                                                    px={4}
                                                    py={2}
                                                    cursor="pointer"
                                                    _hover={{ bg: "surfaceHover" }}
                                                    onClick={() => {
                                                        setFilterEmployeeIds(prev => {
                                                            const sid = String(emp.id);
                                                            if (prev.includes(sid)) return prev.filter(x => x !== sid);
                                                            return [...prev, sid];
                                                        });
                                                    }}
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={3}
                                                >
                                                    <Checkbox
                                                        isChecked={isChecked}
                                                        colorScheme="green"
                                                        borderColor="borderPrimary"
                                                        pointerEvents="none"
                                                    />
                                                    <Avatar size="xs" name={emp.name} src={emp.avatar || emp.url} />
                                                    <Text fontSize="sm" noOfLines={1}>{emp.name}</Text>
                                                </Box>
                                            );
                                        })}
                                    {(!employees || employees.length === 0) && (
                                        <Box p={4} textAlign="center" color="textMuted" fontSize="sm">
                                            Nenhum funcionário encontrado.
                                        </Box>
                                    )}
                                </Stack>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                    <Button
                        leftIcon={<AiOutlinePlus />}
                        size="sm"
                        bg="secondary"
                        color="primary"
                        _hover={{ bg: 'secondaryHover' }}
                        fontWeight="800"
                        flexShrink={0}
                        whiteSpace="nowrap"
                        onClick={() => handleAbrirCard(null, colunas[0]?.id)}
                    >
                        Novo Negócio
                    </Button>
                </HStack>
            </Flex>

            {/* -- Resumo Analítico: Distribuição por Etapa -- */}
            <Box
                bg="surface"
                border="1px solid"
                borderColor="borderPrimary"
                borderRadius="xl"
                p={4}
            >
                <Flex justify="space-between" mb={4} align="center" wrap="wrap" gap={2}>
                    <HStack spacing={2}>
                        <Icon as={AiOutlineFire} color="secondary" boxSize="14px" />
                        <Text fontSize="10px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="2px">
                            Distribuição por Etapa
                        </Text>
                    </HStack>
                    <HStack spacing={4}>
                        <VStack spacing={0} align="flex-end">
                            <Text fontSize="9px" color="textSecondary" fontWeight="700" textTransform="uppercase" letterSpacing="1px">
                                Negócios
                            </Text>
                            <Text fontSize="md" color="textPrimary" fontWeight="900" lineHeight="1">
                                {cardsDoBoard.length}
                            </Text>
                        </VStack>
                        <Box w="1px" h="28px" bg="borderPrimary" />
                        <VStack spacing={0} align="flex-end">
                            <Text fontSize="9px" color="textSecondary" fontWeight="700" textTransform="uppercase" letterSpacing="1px">
                                Pipeline
                            </Text>
                            <Text fontSize="md" color="secondary" fontWeight="900" lineHeight="1">
                                {fmt(totalPipeline)}
                            </Text>
                        </VStack>
                    </HStack>
                </Flex>

                {/* Barra horizontal segmentada (proporcional ao valor) */}
                <Flex
                    h="8px"
                    borderRadius="full"
                    overflow="hidden"
                    bg={colorMode === 'dark' ? "gray.800" : "gray.200"}
                    mb={4}
                    boxShadow="inset 0 1px 3px rgba(0,0,0,0.3)"
                >
                    {colunas.map(col => {
                        const cards = cardsPorColuna[col.id] || [];
                        const valor = cards.filter(c => !c.canceled).reduce((acc, c) => acc + (Number(c.valor) || 0), 0);
                        const pct = totalPipeline > 0 ? (valor / totalPipeline) * 100 : 0;
                        if (pct < 0.5) return null;
                        return (
                            <Tooltip
                                key={col.id}
                                label={`${col.label} • ${cards.length} negócio${cards.length !== 1 ? 's' : ''} • ${fmt(valor)} (${pct.toFixed(1)}%)`}
                                placement="top"
                                hasArrow
                            >
                                <Box
                                    w={`${pct}%`}
                                    bg={col.cor}
                                    h="full"
                                    transition="opacity 0.2s, filter 0.2s"
                                    _hover={{ filter: 'brightness(1.2)' }}
                                    cursor="pointer"
                                />
                            </Tooltip>
                        );
                    })}
                </Flex>

                {/* Lista horizontal de etapas • design clean tipo "funil linear" */}
                <Flex gap={1.5} flexWrap="wrap">
                    {colunas.map(col => {
                        const cards = cardsPorColuna[col.id] || [];
                        const valor = cards.filter(c => !c.canceled).reduce((acc, c) => acc + (Number(c.valor) || 0), 0);
                        const pctValor = totalPipeline > 0 ? (valor / totalPipeline) * 100 : 0;
                        return (
                            <Box
                                key={col.id}
                                flex="1 1 110px"
                                minW="110px"
                                bg={colorMode === 'dark' ? "whiteAlpha.30" : "blackAlpha.50"}
                                borderRadius="lg"
                                px={3}
                                py={2.5}
                                position="relative"
                                overflow="hidden"
                                _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100', transform: 'translateY(-1px)' }}
                                transition="all 0.15s"
                                cursor="default"
                            >
                                {/* Fundo de gradiente sutil baseado na cor */}
                                <Box
                                    position="absolute"
                                    inset={0}
                                    bg={`linear-gradient(135deg, ${col.cor}10 0%, transparent 60%)`}
                                    pointerEvents="none"
                                />

                                {/* Conteúdo */}
                                <Box position="relative">
                                    <HStack spacing={1.5} mb={1.5}>
                                        <Box w="6px" h="6px" borderRadius="full" bg={col.cor} flexShrink={0} boxShadow={`0 0 6px ${col.cor}`} />
                                        <Text
                                            fontSize="9px"
                                            color="textSecondary"
                                            fontWeight="800"
                                            textTransform="uppercase"
                                            letterSpacing="0.8px"
                                            isTruncated
                                            flex={1}
                                        >
                                            {col.label}
                                        </Text>
                                    </HStack>
                                    <HStack align="baseline" spacing={1.5} mb={0.5}>
                                        <Text fontSize="xl" color="textPrimary" fontWeight="900" lineHeight="1">
                                            {cards.length}
                                        </Text>
                                        {valor > 0 && (
                                            <Text fontSize="11px" color={col.cor} fontWeight="800" lineHeight="1" isTruncated>
                                                {fmt(valor)}
                                            </Text>
                                        )}
                                    </HStack>
                                    {/* Mini barra de proporção do valor */}
                                    <Box mt={1.5} h="3px" bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.100"} borderRadius="full" overflow="hidden">
                                        <Box
                                            w={`${Math.max(pctValor, valor > 0 ? 4 : 0)}%`}
                                            h="full"
                                            bg={col.cor}
                                            transition="width 0.4s ease"
                                            borderRadius="full"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Flex>
            </Box>

            {/* -- Próximos Eventos (compromissos agregados) -- */}
            {proximosEventos.length > 0 && (
                <Box
                    bg="surface"
                    border="1px solid"
                    borderColor="borderPrimary"
                    borderRadius="xl"
                    overflow="hidden"
                >
                    <Flex
                        justify="space-between"
                        align="center"
                        px={4}
                        py={3}
                        borderBottom="1px solid"
                        borderColor="borderPrimary"
                    >
                        <HStack spacing={2}>
                            <Icon as={AiOutlineCalendar} color="secondary" boxSize="14px" />
                            <Text fontSize="10px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="2px">
                                Próximos Eventos
                            </Text>
                            <Badge bg={colorMode === 'dark' ? "whiteAlpha.100" : "blackAlpha.100"} color="textSecondary" fontSize="9px" fontWeight="900" px={1.5} borderRadius="sm">
                                {proximosEventos.length}
                            </Badge>
                        </HStack>
                        <Link
                            as={RouterLink}
                            to="/calendario"
                            fontSize="10px"
                            color="secondary"
                            fontWeight="800"
                            textTransform="uppercase"
                            letterSpacing="1px"
                            _hover={{ textDecoration: 'none', color: 'secondaryHover' }}
                        >
                            Ver no calendário →
                        </Link>
                    </Flex>

                    <Box overflowX="auto">
                        <Box minW="700px">
                            {/* Header da tabela */}
                            <Flex
                                px={4} py={2}
                                bg={colorMode === 'dark' ? "whiteAlpha.30" : "blackAlpha.50"}
                                borderBottom="1px solid"
                                borderColor="borderPrimary"
                                fontSize="9px"
                                color="textSecondary"
                                fontWeight="900"
                                textTransform="uppercase"
                                letterSpacing="1.5px"
                            >
                                <Box w="60px" flexShrink={0}>Data</Box>
                                <Box w="80px" flexShrink={0}>Tipo</Box>
                                <Box flex={1} minW={0}>Evento</Box>
                                <Box w="180px" flexShrink={0}>Negócio</Box>
                                <Box w="80px" flexShrink={0} textAlign="right">Quando</Box>
                            </Flex>

                            {/* Linhas */}
                            {proximosEventos.map((evt, idx) => {
                                const tipoMeta = TIPOS_COMPROMISSO.find(t => t.id === evt.tipo) || TIPOS_COMPROMISSO[0];
                                const colunaEvt = pipelineAtivo?.colunas.find(c => c.id === evt.cardColunaId);
                                const hoje = new Date();
                                hoje.setHours(0, 0, 0, 0);
                                const diasAte = Math.ceil((new Date(evt.data + 'T12:00:00') - hoje) / (1000 * 60 * 60 * 24));
                                const isHoje = diasAte === 0;
                                const isAmanha = diasAte === 1;
                                const isAtrasado = diasAte < 0;
                                const labelTempo = isAtrasado ? `${Math.abs(diasAte)}d atrás` : isHoje ? 'Hoje' : isAmanha ? 'Amanhã' : `em ${diasAte}d`;
                                const corTempo = isAtrasado ? 'red.400' : isHoje ? 'secondary' : isAmanha ? '#ECC94B' : 'gray.500';
                                const card = pipelineCards.find(c => c.id === evt.cardId);

                                return (
                                    <Flex
                                        key={`${evt.cardId}-${evt.id}`}
                                        px={4} py={2.5}
                                        align="center"
                                        borderBottom={idx < proximosEventos.length - 1 ? '1px solid' : 'none'}
                                        borderColor="borderPrimary"
                                        _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.30' : 'blackAlpha.50' }}
                                        cursor="pointer"
                                        onClick={() => card && handleAbrirVisualizar(card)}
                                        transition="background 0.1s"
                                    >
                                        {/* Data tipo post-it */}
                                        <Box w="60px" flexShrink={0}>
                                            <Box
                                                bg={`${tipoMeta.cor}15`}
                                                borderRadius="md"
                                                px={2} py={1}
                                                textAlign="center"
                                                w="42px"
                                            >
                                                <Text fontSize="8px" color={tipoMeta.cor} fontWeight="900" textTransform="uppercase" lineHeight="1">
                                                    {new Date(evt.data + 'T12:00:00').toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}
                                                </Text>
                                                <Text fontSize="md" color="textPrimary" fontWeight="900" lineHeight="1">
                                                    {new Date(evt.data + 'T12:00:00').getDate()}
                                                </Text>
                                            </Box>
                                        </Box>

                                        {/* Tipo */}
                                        <Box w="80px" flexShrink={0}>
                                            <Badge
                                                fontSize="8px"
                                                bg={`${tipoMeta.cor}20`}
                                                color={tipoMeta.cor}
                                                fontWeight="900"
                                                px={1.5}
                                                borderRadius="sm"
                                                textTransform="uppercase"
                                            >
                                                {evt.tipo}
                                            </Badge>
                                        </Box>

                                        {/* Evento */}
                                        <Box flex={1} minW={0} pr={2}>
                                            <Text fontSize="xs" color="textPrimary" fontWeight="800" isTruncated>
                                                {evt.titulo}
                                            </Text>
                                            <HStack spacing={2} fontSize="10px" color="textSecondary" mt={0.5}>
                                                {(evt.horaInicio || evt.hora) && <Text fontWeight="700">{evt.horaInicio || evt.hora}</Text>}
                                                {evt.horaFim && <Text>? {evt.horaFim}</Text>}
                                                {evt.local && <Text isTruncated>? {evt.local}</Text>}
                                            </HStack>
                                        </Box>

                                        {/* Negócio vinculado */}
                                        <Box w="180px" flexShrink={0} pr={2}>
                                            <HStack spacing={1.5}>
                                                {colunaEvt && (
                                                    <Box w="6px" h="6px" borderRadius="full" bg={colunaEvt.cor} flexShrink={0} />
                                                )}
                                                <Box minW={0}>
                                                    <Text fontSize="11px" color="textPrimary" fontWeight="700" isTruncated>
                                                        {evt.cardTitulo}
                                                    </Text>
                                                    {evt.cardEmpresa && (
                                                        <Text fontSize="9px" color="textSecondary" isTruncated>
                                                            {evt.cardEmpresa}
                                                        </Text>
                                                    )}
                                                </Box>
                                            </HStack>
                                        </Box>

                                        {/* Quando */}
                                        <Box w="80px" flexShrink={0} textAlign="right">
                                            <Text fontSize="11px" color={corTempo} fontWeight="900" textTransform="uppercase" letterSpacing="0.5px">
                                                {labelTempo}
                                            </Text>
                                        </Box>
                                    </Flex>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>
            )}

            {/* -- Tabela de Atividades Vinculadas ao Pipeline -- */}
            {(() => {
                const atvPipeline = (atividades || []).filter(a => a.ligarPipeline);
                if (atvPipeline.length === 0) return null;

                const TIPO_CORES_ATV = {
                    'Evento': '#02EA75',
                    'Reunião': '#4299E1',
                    'Ligação': '#9F7AEA',
                    'E-mail': '#4299E1',
                    'WhatsApp': '#48BB78',
                    'Visita': '#ED8936',
                    'Tarefa': '#ECC94B',
                    'Prazo': '#FC8181',
                    'Entrega': '#F687B3',
                };

                const STATUS_CORES = {
                    pendente: '#ECC94B',
                    concluido: '#02EA75',
                    cancelado: '#718096',
                };

                const totalPages = Math.ceil(atvPipeline.length / atvItemsPerPage);
                const currentPage = Math.min(atvPage, Math.max(1, totalPages));
                const paginatedAtvPipeline = atvPipeline.slice((currentPage - 1) * atvItemsPerPage, currentPage * atvItemsPerPage);

                return (
                    <Box
                        bg="surface"
                        border="1px solid"
                        borderColor="borderPrimary"
                        borderRadius="xl"
                        overflow="hidden"
                        boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                    >
                        <Flex
                            justify="space-between"
                            align="center"
                            px={5}
                            py={4}
                            borderBottom="1px solid"
                            borderColor="borderPrimary"
                            bg={colorMode === 'dark' ? "whiteAlpha.20" : "blackAlpha.50"}
                        >
                            <HStack spacing={3}>
                                <Icon as={AiOutlineSchedule} color="secondary" boxSize="16px" />
                                <Text fontSize="11px" color="textPrimary" fontWeight="900" textTransform="uppercase" letterSpacing="2px">
                                    Atividades Vinculadas
                                </Text>
                                <Badge bg="secondary" color="primary" fontSize="10px" fontWeight="900" px={2} borderRadius="full">
                                    {atvPipeline.length}
                                </Badge>
                            </HStack>
                            <Link
                                as={RouterLink}
                                to="/comercial/atividades"
                                fontSize="11px"
                                color="secondary"
                                fontWeight="800"
                                textTransform="uppercase"
                                letterSpacing="1px"
                                _hover={{ textDecoration: 'none', color: 'secondaryHover' }}
                                transition="color 0.2s"
                            >
                                Ver todas →
                            </Link>
                        </Flex>

                        <Box overflowX="auto" sx={{
                            '&::-webkit-scrollbar': { height: '6px' },
                            '&::-webkit-scrollbar-track': { bg: 'transparent' },
                            '&::-webkit-scrollbar-thumb': { bg: colorMode === 'dark' ? 'gray.800' : 'gray.300', borderRadius: 'full' },
                        }}>
                            <Box minW="800px">
                                {/* Header */}
                                <Flex
                                    px={5} py={3}
                                    bg={colorMode === 'dark' ? "whiteAlpha.10" : "blackAlpha.50"}
                                    borderBottom="1px solid"
                                    borderColor="borderPrimary"
                                    fontSize="10px"
                                    color="textSecondary"
                                    fontWeight="900"
                                    textTransform="uppercase"
                                    letterSpacing="1.5px"
                                >
                                    <Box w="70px" flexShrink={0}>Data</Box>
                                    <Box w="95px" flexShrink={0}>Tipo</Box>
                                    <Box flex={1} minW={0}>Nome / Detalhes</Box>
                                    <Box w="180px" flexShrink={0}>Responsável</Box>
                                    <Box w="105px" flexShrink={0}>Status</Box>
                                </Flex>

                                {/* Linhas */}
                                {paginatedAtvPipeline.map((atv, idx) => {
                                    const matchingType = (tiposAtividade || []).find(t => t.nome === atv.tipo);
                                    const tipoCor = matchingType?.cor || TIPO_CORES_ATV[atv.tipo] || '#718096';
                                    const statusCor = STATUS_CORES[atv.status] || '#718096';
                                    const dataObj = atv.dataInicio ? new Date(atv.dataInicio + 'T12:00:00') : null;

                                    return (
                                        <Flex
                                            key={atv.id}
                                            px={5} py={3.5}
                                            align="center"
                                            borderBottom={idx < paginatedAtvPipeline.length - 1 ? '1px solid' : 'none'}
                                            borderColor="borderPrimary"
                                            _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50' }}
                                            transition="background 0.2s"
                                            draggable
                                            cursor="pointer"
                                            _active={{ cursor: 'grabbing' }}
                                            opacity={draggedAtividade?.id === atv.id ? 0.5 : 1}
                                            title="Clique para abrir a atividade · arraste para uma coluna do funil para criar um negócio"
                                            onClick={() => navigate(`/comercial/atividades?openActivityId=${atv.id}&mode=view`)}
                                            onDragStart={(e) => {
                                                setDraggedAtividade(atv);
                                                e.dataTransfer.effectAllowed = 'copy';
                                                e.dataTransfer.setData('text/plain', `atv-${atv.id}`);
                                            }}
                                            onDragEnd={() => setDraggedAtividade(null)}
                                        >
                                            {/* Data */}
                                            <Box w="70px" flexShrink={0}>
                                                {dataObj ? (
                                                    <Box
                                                        bg={`${tipoCor}10`}
                                                        border="1px solid"
                                                        borderColor={`${tipoCor}30`}
                                                        borderRadius="lg"
                                                        px={2} py={1.5}
                                                        textAlign="center"
                                                        w="48px"
                                                        boxShadow="sm"
                                                    >
                                                        <Text fontSize="9px" color={tipoCor} fontWeight="900" textTransform="uppercase" lineHeight="1">
                                                            {dataObj.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}
                                                        </Text>
                                                        <Text fontSize="lg" color="textPrimary" fontWeight="950" lineHeight="1.1" mt={0.5}>
                                                            {dataObj.getDate()}
                                                        </Text>
                                                    </Box>
                                                ) : (
                                                    <Text fontSize="xs" color="textSecondary">—</Text>
                                                )}
                                            </Box>

                                            {/* Tipo */}
                                            <Box w="95px" flexShrink={0}>
                                                <Badge
                                                    fontSize="9px"
                                                    bg={`${tipoCor}15`}
                                                    color={tipoCor}
                                                    border="1px solid"
                                                    borderColor={`${tipoCor}35`}
                                                    fontWeight="800"
                                                    px={2.5} py={0.5}
                                                    borderRadius="full"
                                                    textTransform="uppercase"
                                                    letterSpacing="0.5px"
                                                >
                                                    {atv.tipo}
                                                </Badge>
                                            </Box>

                                            {/* Nome */}
                                            <Box flex={1} minW={0} pr={4}>
                                                <HStack spacing={2} wrap="wrap">
                                                    <Link
                                                        as={RouterLink}
                                                        to={{ pathname: "/comercial/atividades", search: `?openActivityId=${atv.id}&mode=view` }}
                                                        state={{ openActivityId: atv.id, mode: 'view' }}
                                                        fontSize="sm"
                                                        color="textPrimary"
                                                        fontWeight="800"
                                                        _hover={{ color: 'secondary', textDecoration: 'none' }}
                                                        isTruncated
                                                    >
                                                        {atv.nome || atv.descricao || '—'}
                                                    </Link>
                                                    {atv.empresa && (
                                                        <Badge
                                                            fontSize="9px"
                                                            bg="#4299E115"
                                                            color="#4299E1"
                                                            border="1px solid"
                                                            borderColor="#4299E135"
                                                            px={1.5}
                                                            borderRadius="md"
                                                            fontWeight="700"
                                                        >
                                                            {atv.empresa}
                                                        </Badge>
                                                    )}
                                                </HStack>
                                                {atv.observacao && (
                                                    <HStack spacing={1} mt={1} color="textSecondary">
                                                        <Icon as={AiOutlineInfoCircle} boxSize="10px" />
                                                        <Text fontSize="11px" isTruncated>
                                                            {atv.observacao}
                                                        </Text>
                                                    </HStack>
                                                )}
                                            </Box>

                                            {/* Responsável */}
                                            <Box w="180px" flexShrink={0} pr={3}>
                                                <HStack spacing={2}>
                                                    <Avatar
                                                        size="xs"
                                                        name={atv.responsavel || '?'}
                                                        bg={colorMode === 'dark' ? "whiteAlpha.100" : "blackAlpha.100"}
                                                        color="textPrimary"
                                                        fontSize="9px"
                                                        fontWeight="900"
                                                    />
                                                    <Text fontSize="xs" color="textPrimary" fontWeight="700" isTruncated>
                                                        {atv.responsavel || '—'}
                                                    </Text>
                                                </HStack>
                                            </Box>

                                            {/* Status */}
                                            <Box w="105px" flexShrink={0}>
                                                <Badge
                                                    fontSize="9px"
                                                    bg={`${statusCor}15`}
                                                    color={statusCor}
                                                    border="1px solid"
                                                    borderColor={`${statusCor}35`}
                                                    fontWeight="800"
                                                    px={2.5} py={0.5}
                                                    borderRadius="full"
                                                    textTransform="uppercase"
                                                    letterSpacing="0.5px"
                                                >
                                                    {atv.status === 'pendente' ? 'Pendente' : atv.status === 'concluido' ? 'Concluído' : 'Cancelado'}
                                                </Badge>
                                            </Box>

                                        </Flex>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Pagination for Linked Activities */}
                        {totalPages > 1 && (
                            <HStack justify="center" spacing={2} py={4} borderTop="1px solid" borderColor="borderPrimary" bg="surfaceRaised">
                                <Button
                                    size="xs"
                                    onClick={() => setAtvPage(p => Math.max(p - 1, 1))}
                                    isDisabled={currentPage === 1}
                                    variant="outline"
                                    borderColor="borderPrimary"
                                    color="textSecondary"
                                    _hover={{ bg: 'surfaceHover', color: 'textPrimary' }}
                                >
                                    Anterior
                                </Button>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                                    <Button
                                        key={pageNumber}
                                        size="xs"
                                        onClick={() => setAtvPage(pageNumber)}
                                        bg={currentPage === pageNumber ? 'secondary' : 'transparent'}
                                        color={currentPage === pageNumber ? 'primary' : 'textSecondary'}
                                        border="1px solid"
                                        borderColor={currentPage === pageNumber ? 'secondary' : 'borderPrimary'}
                                        _hover={{ bg: currentPage === pageNumber ? 'secondaryHover' : 'surfaceHover', color: currentPage === pageNumber ? 'primary' : 'textPrimary' }}
                                        fontWeight="800"
                                    >
                                        {pageNumber}
                                    </Button>
                                ))}
                                <Button
                                    size="xs"
                                    onClick={() => setAtvPage(p => Math.min(p + 1, totalPages))}
                                    isDisabled={currentPage === totalPages}
                                    variant="outline"
                                    borderColor="borderPrimary"
                                    color="textSecondary"
                                    _hover={{ bg: 'surfaceHover', color: 'textPrimary' }}
                                >
                                    Próxima
                                </Button>
                            </HStack>
                        )}
                    </Box>
                );
            })()}

            {/* -- Board container + Tab bar -- */}
            <Box
                bg="boardBg"
                border="1px solid"
                borderColor="borderPrimary"
                borderRadius="xl"
                overflow="hidden"
            >
              {/* Wrapper relativo p/ as zonas laterais de "mover para outro funil"
                  (ficam fixas nas bordas do board, por cima da rolagem horizontal) */}
              <Box position="relative">
                {/* Kanban board */}
                <Box
                    ref={boardScrollRef}
                    overflowX="auto"
                    overflowY="hidden"
                    sx={{
                        '&::-webkit-scrollbar': { height: '8px' },
                        '&::-webkit-scrollbar-track': { bg: colorMode === 'dark' ? 'gray.900' : 'gray.100' },
                        '&::-webkit-scrollbar-thumb': { bg: colorMode === 'dark' ? 'gray.700' : 'gray.300', borderRadius: 'full' },
                        '&::-webkit-scrollbar-thumb:hover': { bg: colorMode === 'dark' ? 'gray.600' : 'gray.400' },
                    }}
                >
                    <HStack align="stretch" spacing={3} p={4} minW="max-content">
                        {colunas.map((coluna, idx) => (
                            <ColunaKanban
                                key={coluna.id}
                                coluna={coluna}
                                cards={cardsPorColuna[coluna.id] || []}
                                isFirst={idx === 0}
                                isLast={idx === colunas.length - 1}
                                largura={colWidth}
                                altura={boardHeight}
                                onResizeLargura={iniciarResizeLargura}
                                onResetLargura={resetLargura}
                                onNovoCard={(presetTitulo) => handleAbrirCard(null, coluna.id, presetTitulo)}
                                onVisualizarCard={handleAbrirVisualizar}
                                onEditarCard={handleAbrirCard}
                                onDeletarCard={handleAbrirExcluir}
                                onMoverCard={handleMoverCard}
                                onCancelarCard={(card) => setAcaoConfirmar({ tipo: 'cancelar', card })}
                                onReativarCard={(card) => setAcaoConfirmar({ tipo: 'reativar', card })}
                                onEditarColuna={handleAbrirRenomear}
                                onDeletarColuna={(colunaId) => {
                                    const col = colunas.find(c => String(c.id) === String(colunaId));
                                    setColunaExcluindo(col || { id: colunaId, label: '' });
                                    onDelColunaOpen();
                                }}
                                isDragOver={dragOverColId === coluna.id}
                                onDragStartCard={handleDragStart}
                                onDragEndCard={handleDragEnd}
                                onDragOverColuna={handleDragOverColuna}
                                onDropColuna={handleDropColuna}
                                currentEmployee={currentEmployee}
                            />
                        ))}

                        {/* Adicionar coluna */}
                        <Box
                            w="220px"
                            minW="220px"
                            minH="120px"
                            border="2px dashed"
                            borderColor="borderPrimary"
                            borderRadius="xl"
                            cursor="pointer"
                            onClick={onColunaOpen}
                            _hover={{ borderColor: 'secondary', bg: colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50' }}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexDirection="column"
                            gap={2}
                            color="textSecondary"
                            transition="all 0.2s"
                            p={4}
                        >
                            <Icon as={AiOutlinePlus} boxSize="22px" />
                            <Text fontSize="xs" fontWeight="700" textAlign="center">Nova Coluna</Text>
                        </Box>
                    </HStack>
                </Box>

                {/* Zonas laterais: arraste um card até a borda para movê-lo de funil.
                    Aparecem só durante o arraste de um CARD (não de atividade). */}
                {draggedCardId && !draggedAtividade && ['prev', 'next'].map(dir => {
                    const vizinho = funilVizinho(dir);
                    const ativo = dragOverFunilDir === dir;
                    const temFunil = !!vizinho;
                    return (
                        <Box
                            key={dir}
                            position="absolute"
                            top={0}
                            bottom={0}
                            {...(dir === 'next' ? { right: 0 } : { left: 0 })}
                            w={{ base: '64px', md: '96px' }}
                            zIndex={20}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            gap={2}
                            px={2}
                            textAlign="center"
                            cursor={temFunil ? 'copy' : 'not-allowed'}
                            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = temFunil ? 'move' : 'none'; if (dragOverFunilDir !== dir) setDragOverFunilDir(dir); }}
                            onDragLeave={() => setDragOverFunilDir(prev => prev === dir ? null : prev)}
                            onDrop={(e) => handleDropFunilVizinho(e, dir)}
                            bg={ativo && temFunil
                                ? 'rgba(1,233,117,0.18)'
                                : (colorMode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.7)')}
                            borderLeft={dir === 'next' ? '2px dashed' : 'none'}
                            borderRight={dir === 'prev' ? '2px dashed' : 'none'}
                            borderColor={ativo && temFunil ? 'secondary' : 'borderPrimary'}
                            backdropFilter="blur(2px)"
                            transition="background 0.12s, border-color 0.12s"
                            opacity={temFunil ? 1 : 0.85}
                        >
                            <Icon
                                as={dir === 'next' ? AiOutlineRight : AiOutlineLeft}
                                boxSize="20px"
                                color={ativo && temFunil ? 'secondary' : (temFunil ? 'textSecondary' : 'textMuted')}
                            />
                            <Text
                                fontSize="10px"
                                fontWeight="800"
                                lineHeight="1.2"
                                color={ativo && temFunil ? 'secondary' : (temFunil ? 'textSecondary' : 'textMuted')}
                                noOfLines={3}
                            >
                                {temFunil
                                    ? `Mover para "${vizinho.nome}"`
                                    : (dir === 'next' ? 'Não há funil à direita' : 'Não há funil à esquerda')}
                            </Text>
                        </Box>
                    );
                })}
              </Box>

                {/* Alça de altura do painel: arraste para baixo/cima; duplo clique = automático */}
                <Flex
                    h="14px"
                    align="center"
                    justify="center"
                    cursor="row-resize"
                    borderTop="1px solid"
                    borderColor="borderPrimary"
                    bg={colorMode === 'dark' ? 'whiteAlpha.50' : 'blackAlpha.50'}
                    _hover={{ bg: 'secondarySubtle' }}
                    onPointerDown={iniciarResizeAltura}
                    onDoubleClick={resetAltura}
                    title="Arraste para ajustar a altura do painel (duplo clique restaura o automático)"
                    style={{ touchAction: 'none' }}
                >
                    <Box w="46px" h="4px" borderRadius="full" bg="borderPrimary" pointerEvents="none" />
                </Flex>

                {/* -- Tab bar -- */}
                <Flex
                    borderTop="1px solid"
                    borderColor="borderPrimary"
                    bg="boardBg"
                    align="stretch"
                    minH="40px"
                    overflowX="auto"
                    sx={{ '&::-webkit-scrollbar': { height: '0px' } }}
                >
                    {pipelines.map(p => {
                        const ativo = p.id === pipelineAtivo.id;
                        return (
                            <Flex
                                key={p.id}
                                align="center"
                                gap={2}
                                px={4}
                                py={0}
                                cursor="pointer"
                                borderRight="1px solid"
                                borderColor="borderPrimary"
                                bg={ativo ? 'surface' : 'transparent'}
                                borderTop="2px solid"
                                borderTopColor={ativo ? 'secondary' : 'transparent'}
                                _hover={{ bg: ativo ? 'surface' : (colorMode === 'dark' ? 'gray.800' : 'gray.200') }}
                                onClick={() => handleSetPipelineAtivo(p.id)}
                                minW="max-content"
                                transition="background 0.15s"
                            >
                                <Box w="8px" h="8px" borderRadius="full" bg={p.cor} flexShrink={0} />
                                <Text
                                    fontSize="xs"
                                    fontWeight={ativo ? '800' : '600'}
                                    color={ativo ? 'textPrimary' : 'textSecondary'}
                                    userSelect="none"
                                >
                                    {p.nome}
                                </Text>
                                <HStack spacing={1} ml={1}>
                                    <Tooltip label="Renomear" fontSize="xs">
                                        <Box display="inline-block">
                                            <Icon
                                                as={AiOutlineEdit}
                                                boxSize="11px"
                                                color="textSecondary"
                                                _hover={{ color: 'secondary' }}
                                                onClick={(e) => handleAbrirRenomearAba(p, e)}
                                            />
                                        </Box>
                                    </Tooltip>
                                    {pipelines.length > 1 && (
                                        <Tooltip label="Excluir aba" fontSize="xs">
                                            <Box display="inline-block">
                                                <Icon
                                                    as={AiOutlineClose}
                                                    boxSize="10px"
                                                    color="textMuted"
                                                    _hover={{ color: 'red.400' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPipelineExcluindo(p);
                                                        onDelPipelineOpen();
                                                    }}
                                                />
                                            </Box>
                                        </Tooltip>
                                    )}
                                </HStack>
                            </Flex>
                        );
                    })}

                    <Flex
                        align="center"
                        px={3}
                        cursor="pointer"
                        color="textMuted"
                        _hover={{ color: 'secondary', bg: 'gray.800' }}
                        onClick={onAbaOpen}
                        minW="40px"
                        borderRight="1px solid"
                        borderColor="borderPrimary"
                    >
                        <Icon as={AiOutlinePlus} boxSize="14px" />
                    </Flex>
                </Flex>
            </Box>

            {/* ------------------------------------------------------?
                Modal: Criar/Editar Negócio
            ----------------------------------------------------?-- */}
            <Modal isOpen={isCardOpen} onClose={handleMinimizarCard} isCentered size="4xl" scrollBehavior="inside" closeOnOverlayClick={true}>
                <ModalOverlay backdropFilter="blur(8px)" bg="rgba(0,0,0,0.4)" />
                <ModalContent
                    bg={modalGlassBg}
                    border="1px solid"
                    borderColor={modalGlassBorder}
                    color="textPrimary"
                    onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSalvarCard(); }}
                >
                    {/* Header */}
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" pb={4} pr="90px">
                        <HStack spacing={3}>
                            <Flex
                                w="32px" h="32px"
                                bg="surfaceHover"
                                borderRadius="lg"
                                align="center"
                                justify="center"
                                flexShrink={0}
                            >
                                <Icon as={AiOutlinePartition} color="secondary" boxSize="16px" />
                            </Flex>
                            <Box>
                                <Text fontWeight="800" fontSize="md" lineHeight="1.2">
                                    {cardEditando ? 'Editar Negócio' : 'Novo Negócio'}
                                </Text>
                                {cardColunaId && funilForm && (() => {
                                    const col = (funilForm.colunas || []).find(c => c.id === cardColunaId);
                                    return col ? (
                                        <HStack spacing={1.5} mt={0.5}>
                                            <Box w="6px" h="6px" borderRadius="full" bg={col.cor} />
                                            <Text fontSize="xs" color="textSecondary" fontWeight="600">{funilForm.nome} · {col.label}</Text>
                                        </HStack>
                                    ) : null;
                                })()}
                            </Box>
                            {cardEditando && formCard.valor > 0 && (
                                <Badge
                                    ml="auto"
                                    mr={2}
                                    bg="surfaceHover"
                                    color="secondary"
                                    fontSize="sm"
                                    fontWeight="900"
                                    px={3}
                                    py={1}
                                    borderRadius="lg"
                                >
                                    {fmt(formCard.valor)}
                                </Badge>
                            )}
                        </HStack>
                    </ModalHeader>

                    {/* Botões no topo: Minimizar e Fechar */}
                    <HStack position="absolute" top={4} right={4} spacing={1} zIndex={10}>
                        <IconButton
                            aria-label="Minimizar"
                            title="Minimizar"
                            icon={<Icon as={AiOutlineMinus} boxSize="16px" />}
                            size="sm"
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ bg: "whiteAlpha.200", color: "secondary" }}
                            borderRadius="md"
                            onClick={handleMinimizarCard}
                        />
                        <ModalCloseButton position="relative" top={0} right={0} color="textSecondary" onClick={handleFecharCard} />
                    </HStack>

                    <ModalBody py={5}>
                        <VStack spacing={5} align="stretch">

                            {/* -- Seção: Negócio -- */}
                            <SecaoLabel label="Negócio" />
                            <FormControl isRequired>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Título do Negócio
                                </FormLabel>
                                <Input
                                    value={formCard.titulo}
                                    onChange={e => setFormCard(p => ({ ...p, titulo: e.target.value }))}
                                    placeholder="Ex: ERP + App Completo"
                                    bg={inputGlassBg}
                                    borderColor={modalGlassBorder}
                                    _hover={{ borderColor: inputGlassBorderHover }}
                                    _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    fontWeight="700"
                                    size="md"
                                    autoFocus
                                />
                            </FormControl>

                            {/* -- Seção: Contato -- */}
                            <SecaoLabel label="Contato" />
                            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3} alignItems="stretch">
                                <Box
                                    bg={colorMode === 'dark' ? "whiteAlpha.30" : "blackAlpha.30"}
                                    border="1px solid"
                                    borderColor={marcaVinculada ? '#4299E150' : (formCard.clienteNovo ? '#ED893650' : modalGlassBorder)}
                                    borderRadius="xl"
                                    p={3}
                                    minH="210px"
                                >
                                    <HStack justify="space-between" mb={3} align="center">
                                        <HStack spacing={2}>
                                            <Flex w="28px" h="28px" borderRadius="lg" align="center" justify="center" bg="#4299E116">
                                                <Icon as={AiOutlineBank} color="#4299E1" boxSize="14px" />
                                            </Flex>
                                            <Box>
                                                <Text fontSize="11px" color="textPrimary" fontWeight="900" textTransform="uppercase" letterSpacing="0.8px">Marca / Empresa</Text>
                                                <Text fontSize="10px" color="textMuted">Fonte comercial do negócio</Text>
                                            </Box>
                                        </HStack>
                                        <Badge bg={marcaVinculada ? '#4299E120' : (formCard.clienteNovo ? '#ED893620' : 'surfaceHover')} color={marcaVinculada ? '#4299E1' : (formCard.clienteNovo ? '#ED8936' : 'textSecondary')} fontSize="8px" fontWeight="900" px={1.5}>
                                            {marcaVinculada ? 'ERP' : (formCard.clienteNovo ? 'Novo' : 'Empresa')}
                                        </Badge>
                                    </HStack>

                                    <FormControl>
                                        <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">Buscar marca</FormLabel>
                                        <Box position="relative" ref={empresaInputRef}>
                                            <InputGroup>
                                                <InputLeftElement pointerEvents="none"><Icon as={AiOutlineSearch} color="textMuted" boxSize="14px" /></InputLeftElement>
                                                <Input
                                                    value={formCard.empresa}
                                                    onChange={e => { setFormCard(p => ({ ...p, empresa: e.target.value, marcaId: null })); setEmpresaDropdownAberto(true); }}
                                                    onFocus={() => setEmpresaDropdownAberto(true)}
                                                    onBlur={() => setTimeout(() => setEmpresaDropdownAberto(false), 180)}
                                                    placeholder="Buscar marca, empresa..."
                                                    bg={inputGlassBg}
                                                    borderColor={marcaVinculada ? '#4299E1' : (formCard.clienteNovo ? '#ED8936' : modalGlassBorder)}
                                                    _hover={{ borderColor: marcaVinculada ? '#4299E1' : (formCard.clienteNovo ? '#ED8936' : inputGlassBorderHover) }}
                                                    _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                                    pl={9}
                                                />
                                            </InputGroup>
                                            {empresaDropdownAberto && formCard.empresa && (
                                                <Box position="absolute" top="calc(100% + 4px)" left={0} right={0} zIndex={9999} bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden" boxShadow="0 12px 40px rgba(0,0,0,0.6)" maxH="320px" overflowY="auto">
                                                    {marcasFiltradas.length > 0 && (<>
                                                        <Box px={3} py={1.5} bg="surfaceHover"><HStack spacing={1.5}><Icon as={AiOutlineDatabase} color="#4299E1" boxSize="10px" /><Text fontSize="9px" color="#4299E1" fontWeight="900" letterSpacing="1.5px" textTransform="uppercase">Cadastro ERP • {marcasFiltradas.length}</Text></HStack></Box>
                                                        {marcasFiltradas.map(m => (
                                                            <Box key={`marca-${m.id}`} px={3} py={2.5} cursor="pointer" _hover={{ bg: 'surfaceHover' }} onMouseDown={() => handleSelecionarMarca(m)} borderBottom="1px solid" borderColor="borderPrimary">
                                                                <HStack spacing={2.5}>
                                                                    <Avatar size="xs" name={m.nome} src={m.logo} bg={m.cor_principal || 'gray.600'} color="textPrimary" fontSize="9px" fontWeight="900" sx={{ '& img': { objectFit: 'contain', p: '2px' } }} />
                                                                    <Box flex={1} minW={0}><Text fontSize="sm" color="textPrimary" fontWeight="700" lineHeight="1.2" isTruncated>{m.nome}</Text><Text fontSize="xs" color="textMuted" isTruncated>{m.provedor ? m.provedor : 'Sem ERP'}</Text></Box>
                                                                    <Badge fontSize="8px" bg="#4299E120" color="#4299E1" fontWeight="900" px={1.5}>ERP</Badge>
                                                                </HStack>
                                                            </Box>
                                                        ))}
                                                    </>)}
                                                    <Box px={3} py={2.5} cursor="pointer" _hover={{ bg: 'surfaceHover' }} onMouseDown={handleUsarClienteNovo} bg={temResultados ? 'transparent' : 'surfaceRaised'} borderTop={temResultados ? '2px solid' : 'none'} borderColor="borderPrimary">
                                                        <HStack spacing={2.5}>
                                                            <Flex w="24px" h="24px" bg="#ED893620" borderRadius="full" align="center" justify="center" flexShrink={0}><Icon as={AiOutlineUserAdd} color="#ED8936" boxSize="12px" /></Flex>
                                                            <Box flex={1} minW={0}><Text fontSize="sm" color="textPrimary" fontWeight="700" lineHeight="1.2">Usar "{formCard.empresa}" como empresa nova</Text><Text fontSize="xs" color="textMuted">{temResultados ? 'Não está nas opções acima' : 'Não encontrado no ERP'}</Text></Box>
                                                            <Badge fontSize="8px" bg="#ED893620" color="#ED8936" fontWeight="900" px={1.5}>Novo</Badge>
                                                        </HStack>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </FormControl>

                                    <Box mt={3} pt={3} borderTop="1px solid" borderColor="borderPrimary">
                                        {marcaVinculada ? (
                                            <HStack spacing={3} align="flex-start">
                                                <Avatar size="sm" name={marcaVinculada.nome} src={marcaVinculada.logo} bg={marcaVinculada.cor_principal || '#4299E1'} color="white" fontWeight="900" fontSize="11px" flexShrink={0} />
                                                <Box flex={1} minW={0}>
                                                    <Text fontSize="sm" color="textPrimary" fontWeight="900" lineHeight="1.2" isTruncated>{marcaVinculada.nome}</Text>
                                                    <Text fontSize="xs" color="textSecondary" mt={0.5} noOfLines={2}>{marcaVinculada.provedor || 'Sem ERP definido'}{marcaVinculada.data_cadastro ? ` • Cadastro ${new Date(marcaVinculada.data_cadastro).toLocaleDateString('pt-BR')}` : ''}</Text>
                                                    <HStack spacing={3} mt={1.5} flexWrap="wrap">
                                                        {marcaVinculada.responsavel_marca_nome && (<HStack spacing={1}><Icon as={AiOutlineUser} color="textMuted" boxSize="11px" /><Text fontSize="xs" color="textSecondary" noOfLines={1}>{marcaVinculada.responsavel_marca_nome}</Text></HStack>)}
                                                        {marcaVinculada.whatsapp && (<HStack spacing={1}><Icon as={AiOutlinePhone} color="textMuted" boxSize="11px" /><Text fontSize="xs" color="textSecondary">{marcaVinculada.whatsapp}</Text></HStack>)}
                                                    </HStack>
                                                </Box>
                                            </HStack>
                                        ) : formCard.clienteNovo ? (
                                            <HStack spacing={3} align="center"><Flex w="34px" h="34px" bg="#ED893620" borderRadius="full" align="center" justify="center" flexShrink={0}><Icon as={AiOutlineWarning} color="#ED8936" boxSize="15px" /></Flex><Box><Text fontSize="sm" color="textPrimary" fontWeight="900">Cliente novo sem cadastro</Text><Text fontSize="xs" color="textSecondary" mt={0.5}>Depois de fechar o negócio, considere cadastrar como Contato ou Marca no ERP.</Text></Box></HStack>
                                        ) : (<Text fontSize="xs" color="textMuted">Selecione uma marca do ERP ou use uma empresa nova.</Text>)}
                                    </Box>
                                </Box>

                                <Box bg={colorMode === 'dark' ? "whiteAlpha.30" : "blackAlpha.30"} border="1px solid" borderColor={contatoSelecionado ? '#02EA7550' : modalGlassBorder} borderRadius="xl" p={3} minH="210px">
                                    <HStack justify="space-between" mb={3} align="center">
                                        <HStack spacing={2}><Flex w="28px" h="28px" borderRadius="lg" align="center" justify="center" bg="#02EA7516"><Icon as={AiOutlineContacts} color="secondary" boxSize="14px" /></Flex><Box><Text fontSize="11px" color="textPrimary" fontWeight="900" textTransform="uppercase" letterSpacing="0.8px">Contato</Text><Text fontSize="10px" color="textMuted">Pessoa vinculada à oportunidade</Text></Box></HStack>
                                        <Badge bg={contatoSelecionado ? '#02EA7520' : 'surfaceHover'} color={contatoSelecionado ? 'secondary' : 'textSecondary'} fontSize="8px" fontWeight="900" px={1.5}>{contatoSelecionado ? 'CRM' : 'Contato'}</Badge>
                                    </HStack>
                                    <FormControl>
                                        <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">Buscar contato</FormLabel>
                                        <Box position="relative">
                                            <InputGroup><InputLeftElement pointerEvents="none"><Icon as={AiOutlineSearch} color="textMuted" boxSize="14px" /></InputLeftElement><Input value={formCard.contato} onChange={e => { setFormCard(p => ({ ...p, contato: e.target.value })); setBuscaContato(e.target.value); setContatoFocado(true); }} onFocus={() => setContatoFocado(true)} onBlur={() => setTimeout(() => setContatoFocado(false), 180)} placeholder="Pesquisar contato..." bg={inputGlassBg} borderColor={modalGlassBorder} _hover={{ borderColor: inputGlassBorderHover }} _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }} pl={9} /></InputGroup>
                                            {contatoFocado && formCard.contato && (
                                                <Box position="absolute" top="calc(100% + 4px)" left={0} right={0} zIndex={9999} bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden" boxShadow="0 12px 40px rgba(0,0,0,0.6)" maxH="240px" overflowY="auto">
                                                    {contatosSugeridos.length > 0 ? (contatosSugeridos.map(c => (<Box key={`sug-cont-${c.id}`} px={3} py={2.5} cursor="pointer" _hover={{ bg: 'surfaceHover' }} onMouseDown={() => handleSelecionarContatoCard(c)} borderBottom="1px solid" borderColor="borderPrimary"><Text fontSize="sm" color="textPrimary" fontWeight="700">{c.nome}</Text>{(c.cargo || c.empresa) && (<Text fontSize="xs" color="textMuted">{[c.cargo, c.empresa].filter(Boolean).join(' • ')}</Text>)}{c.telefone && (<Text fontSize="xs" color="textSecondary">{c.telefone}</Text>)}</Box>))) : (<Box px={3} py={2.5}><Text fontSize="sm" color="textMuted">Nenhum contato encontrado.</Text><Text fontSize="xs" color="textSecondary" mt={1}>Pressione Enter para usar "{formCard.contato}" de forma livre.</Text></Box>)}
                                                </Box>
                                            )}
                                        </Box>
                                    </FormControl>
                                    <Box mt={3} pt={3} borderTop="1px solid" borderColor="borderPrimary">
                                        {contatoSelecionado ? (
                                            <HStack spacing={3} align="flex-start"><Avatar size="sm" name={contatoSelecionado.nome} bg="secondary" color="primary" fontWeight="900" fontSize="11px" flexShrink={0} /><Box flex={1} minW={0}><HStack spacing={2} align="center"><Text fontSize="sm" color="textPrimary" fontWeight="900" lineHeight="1.2" isTruncated>{contatoSelecionado.nome}</Text>{contatoSelecionado.canal && (<Badge bg="surfaceHover" color="textSecondary" fontSize="8px" fontWeight="800" px={1.5}>{contatoSelecionado.canal}</Badge>)}</HStack>{(contatoSelecionado.cargo || contatoSelecionado.empresa) && (<Text fontSize="xs" color="textSecondary" mt={0.5} noOfLines={2}>{[contatoSelecionado.cargo, contatoSelecionado.empresa].filter(Boolean).join(' • ')}</Text>)}<HStack spacing={3} mt={1.5} flexWrap="wrap">{contatoSelecionado.telefone && (<HStack spacing={1}><Icon as={AiOutlinePhone} color="textMuted" boxSize="11px" /><Text fontSize="xs" color="textSecondary">{contatoSelecionado.telefone}</Text></HStack>)}{contatoSelecionado.email && (<HStack spacing={1}><Icon as={AiOutlineMail} color="textMuted" boxSize="11px" /><Text fontSize="xs" color="textSecondary">{contatoSelecionado.email}</Text></HStack>)}</HStack></Box></HStack>
                                        ) : (<Text fontSize="xs" color="textMuted"></Text>)}
                                    </Box>
                                </Box>
                            </SimpleGrid>

                            {/* -- Seção: Financeiro -- */}
                            <SecaoLabel label="Financeiro" />
                            <HStack spacing={3} align="flex-start">
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Valor (R$)
                                    </FormLabel>
                                    <NumberInput
                                        value={formCard.valor}
                                        onChange={val => setFormCard(p => ({ ...p, valor: val }))}
                                        min={0}
                                    >
                                        <NumberInputField
                                            placeholder="0"
                                            bg={inputGlassBg}
                                            borderColor={modalGlassBorder}
                                            _hover={{ borderColor: inputGlassBorderHover }}
                                            _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        />
                                    </NumberInput>
                                    {Number(formCard.valor) > 0 && (
                                        <Text fontSize="11px" color="secondary" fontWeight="800" mt={1} pl={1}>
                                            {fmt(formCard.valor)}
                                        </Text>
                                    )}
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Probabilidade (%)
                                    </FormLabel>
                                    <NumberInput
                                        value={formCard.probabilidade}
                                        onChange={val => setFormCard(p => ({ ...p, probabilidade: Number(val) }))}
                                        min={0} max={100}
                                    >
                                        <NumberInputField
                                            bg={inputGlassBg}
                                            borderColor={modalGlassBorder}
                                            _hover={{ borderColor: inputGlassBorderHover }}
                                            _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        />
                                    </NumberInput>
                                    <Box mt={1.5} px={0.5}>
                                        <Progress
                                            value={formCard.probabilidade || 0}
                                            size="xs"
                                            bg="surfaceHover"
                                            borderRadius="full"
                                            sx={{
                                                '& > div': {
                                                    background: corProb,
                                                    transition: 'width 0.3s ease, background 0.3s ease',
                                                }
                                            }}
                                        />
                                    </Box>
                                </FormControl>
                            </HStack>

                            {/* -- Seção: Pipeline -- */}
                            <SecaoLabel label="Pipeline" />
                            <HStack spacing={3} align="flex-start">
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Funil
                                    </FormLabel>
                                    <SearchableSelect
                                        value={funilForm?.id || ''}
                                        onChange={v => {
                                            const novoFunil = (pipelines || []).find(p => String(p.id) === String(v));
                                            if (!novoFunil) return;
                                            setFunilFormId(novoFunil.id);
                                            // Voltou ao funil original do card → restaura a etapa original;
                                            // funil diferente → primeira etapa (por ordem) do funil novo.
                                            if (cardEditando && String(cardEditando.pipelineId) === String(novoFunil.id)) {
                                                setCardColunaId(cardEditando.colunaId);
                                            } else {
                                                const colsNovoFunil = [...(novoFunil.colunas || [])].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
                                                setCardColunaId(colsNovoFunil[0]?.id || '');
                                            }
                                        }}
                                        options={(pipelines || []).map(p => ({ value: p.id, label: p.nome }))}
                                        placeholder="Buscar funil..."
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Etapa / Coluna
                                    </FormLabel>
                                    <SearchableSelect
                                        value={cardColunaId}
                                        onChange={v => setCardColunaId(v)}
                                        options={colunasFormulario.map(c => ({ value: c.id, label: c.label }))}
                                        placeholder="Buscar etapa..."
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Prioridade
                                    </FormLabel>
                                    <HStack spacing={2} mt={1}>
                                        {PRIORIDADES.map(p => (
                                            <Box
                                                key={p.id}
                                                px={3} py={1.5}
                                                borderRadius="lg"
                                                cursor="pointer"
                                                bg={formCard.prioridade === p.id ? `${p.cor}25` : 'surfaceRaised'}
                                                border="1px solid"
                                                borderColor={formCard.prioridade === p.id ? p.cor : 'borderPrimary'}
                                                color={formCard.prioridade === p.id ? p.cor : 'textMuted'}
                                                fontSize="xs"
                                                fontWeight="700"
                                                onClick={() => setFormCard(prev => ({ ...prev, prioridade: p.id }))}
                                                transition="all 0.15s"
                                                _hover={{ borderColor: p.cor, color: p.cor }}
                                                flex={1}
                                                textAlign="center"
                                            >
                                                {p.id}
                                            </Box>
                                        ))}
                                    </HStack>
                                </FormControl>
                            </HStack>

                            {/* -- Seção: Detalhes -- */}
                            <SecaoLabel label="Detalhes" />
                            <SimpleGrid columns={4} spacing={3}>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Canal de Origem
                                    </FormLabel>
                                    <SearchableSelect
                                        value={formCard.canal}
                                        onChange={v => setFormCard(p => ({ ...p, canal: v || '' }))}
                                        options={opcoesCanal}
                                        placeholder="Buscar canal..."
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Data de Início
                                    </FormLabel>
                                    <Input
                                        type="date"
                                        value={formCard.dataInicio}
                                        onChange={e => setFormCard(p => ({ ...p, dataInicio: e.target.value }))}
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        sx={{ colorScheme: 'dark' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Previsão de Fechamento
                                    </FormLabel>
                                    <Input
                                        type="date"
                                        value={formCard.dataFechamento}
                                        onChange={e => setFormCard(p => ({ ...p, dataFechamento: e.target.value }))}
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        sx={{ colorScheme: 'dark' }}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                        Resultado
                                    </FormLabel>
                                    <SearchableSelect
                                        value={formCard.result_id || ''}
                                        onChange={v => setFormCard(p => ({ ...p, result_id: v ? Number(v) : null, completed: !!v }))}
                                        options={(resultadosNegocio || []).map(r => ({ value: r.id, label: r.nome }))}
                                        isClearable
                                        placeholder="Em aberto"
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                    />
                                </FormControl>
                            </SimpleGrid>

                            <FormControl position="relative">
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Responsável
                                </FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={AiOutlineIdcard} color="textMuted" boxSize="14px" />
                                    </InputLeftElement>
                                    <Input
                                        value={formCard.responsavel}
                                        onChange={e => setFormCard(p => ({ ...p, responsavel: e.target.value }))}
                                        onFocus={() => setResponsavelFocado(true)}
                                        onBlur={() => setTimeout(() => setResponsavelFocado(false), 200)}
                                        placeholder="Nome do responsável"
                                        bg={inputGlassBg}
                                        borderColor={modalGlassBorder}
                                        _hover={{ borderColor: inputGlassBorderHover }}
                                        _focus={{ bg: inputGlassFocusBg, borderColor: 'secondary', boxShadow: '0 0 0 1px var(--chakra-colors-secondary)' }}
                                        pl={9}
                                    />
                                </InputGroup>
                                {responsavelFocado && responsaveisSugeridos.length > 0 && (
                                    <Box
                                        position="absolute"
                                        top="100%"
                                        left={0}
                                        right={0}
                                        bg="surfaceRaised"
                                        border="1px solid"
                                        borderColor="borderPrimary"
                                        borderRadius="md"
                                        zIndex={10000}
                                        maxH="200px"
                                        overflowY="auto"
                                        boxShadow="0 4px 12px rgba(0,0,0,0.5)"
                                    >
                                        {responsaveisSugeridos.map(emp => (
                                            <Box
                                                key={emp.id}
                                                px={3}
                                                py={2}
                                                cursor="pointer"
                                                _hover={{ bg: 'surfaceHover', color: 'secondary' }}
                                                onMouseDown={() => handleSelecionarResponsavelCard(emp)}
                                                fontSize="sm"
                                                color="textPrimary"
                                            >
                                                {emp.name}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </FormControl>

                            {/* -- Seção: Agenda / Compromissos -- */}
                            <SecaoLabel label="Agenda • Compromissos" />
                            <VStack align="stretch" spacing={2}>
                                {/* Lista de compromissos existentes */}
                                {(formCard.compromissos || []).length === 0 && !novoCompromissoAberto && (
                                    <Box
                                        bg="surfaceRaised"
                                        border="1px dashed"
                                        borderColor="borderPrimary"
                                        borderRadius="lg"
                                        py={3}
                                        textAlign="center"
                                    >
                                        <Text fontSize="xs" color="textMuted">
                                            Nenhum compromisso agendado
                                        </Text>
                                    </Box>
                                )}

                                {(formCard.compromissos || []).map(comp => {
                                    const tipoMeta = TIPOS_COMPROMISSO.find(t => t.id === comp.tipo) || TIPOS_COMPROMISSO[0];
                                    return (
                                        <Box
                                            key={comp.id}
                                            bg="surfaceHover"
                                            border="1px solid"
                                            borderColor={comp.concluido ? 'gray.700' : `${tipoMeta.cor}40`}
                                            borderRadius="lg"
                                            p={2.5}
                                            position="relative"
                                            opacity={comp.concluido ? 0.6 : 1}
                                            transition="all 0.15s"
                                            _hover={{ borderColor: tipoMeta.cor }}
                                            cursor="pointer"
                                            onClick={() => handleEditarCompromisso(comp)}
                                        >
                                            <HStack spacing={2.5} align="flex-start">
                                                {/* Checkbox de concluído */}
                                                <Box
                                                    as="button"
                                                    w="18px" h="18px"
                                                    borderRadius="md"
                                                    border="2px solid"
                                                    borderColor={comp.concluido ? tipoMeta.cor : 'gray.600'}
                                                    bg={comp.concluido ? tipoMeta.cor : 'transparent'}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    onClick={(e) => { e.stopPropagation(); handleToggleCompromissoConcluido(comp.id); }}
                                                    flexShrink={0}
                                                    mt={0.5}
                                                    _hover={{ borderColor: tipoMeta.cor }}
                                                    transition="all 0.15s"
                                                >
                                                    {comp.concluido && (
                                                        <Icon as={AiOutlineCheck} color="textPrimary" boxSize="10px" />
                                                    )}
                                                </Box>

                                                <Box flex={1} minW={0}>
                                                    <HStack spacing={1.5} mb={0.5}>
                                                        <Badge
                                                            fontSize="8px"
                                                            bg={`${tipoMeta.cor}20`}
                                                            color={tipoMeta.cor}
                                                            fontWeight="900"
                                                            px={1.5}
                                                            borderRadius="sm"
                                                            textTransform="uppercase"
                                                        >
                                                            {comp.tipo}
                                                        </Badge>
                                                        <Text
                                                            fontSize="xs"
                                                            color="textPrimary"
                                                            fontWeight="800"
                                                            textDecoration={comp.concluido ? 'line-through' : 'none'}
                                                            isTruncated
                                                            flex={1}
                                                        >
                                                            {comp.titulo}
                                                        </Text>
                                                    </HStack>
                                                    <HStack spacing={2.5} fontSize="10px" color="textMuted" flexWrap="wrap">
                                                        <HStack spacing={1}>
                                                            <Icon as={AiOutlineCalendar} boxSize="10px" />
                                                            <Text fontWeight="700">
                                                                {new Date(comp.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                                                            </Text>
                                                        </HStack>
                                                        {comp.dataFim && comp.dataFim !== comp.data && (
                                                            <Text fontWeight="700">? {new Date(comp.dataFim + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</Text>
                                                        )}
                                                        {(comp.horaInicio || comp.hora) && (
                                                            <Text fontWeight="700">? {comp.horaInicio || comp.hora}{comp.horaFim ? `?${comp.horaFim}` : ''}</Text>
                                                        )}
                                                        {comp.local && (
                                                            <Text isTruncated>? {comp.local}</Text>
                                                        )}
                                                        {comp.responsavel && (
                                                            <Text isTruncated>? Responsável: {comp.responsavel}</Text>
                                                        )}
                                                    </HStack>
                                                    {comp.descricao && (
                                                        <Text fontSize="11px" color="textSecondary" mt={1} noOfLines={2}>
                                                            {comp.descricao}
                                                        </Text>
                                                    )}
                                                </Box>

                                                <IconButton
                                                    aria-label="Editar"
                                                    icon={<AiOutlineEdit />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="textMuted"
                                                    _hover={{ color: 'secondary', bg: 'rgba(2,234,117,0.1)' }}
                                                    onClick={(e) => { e.stopPropagation(); handleEditarCompromisso(comp); }}
                                                    minW="22px" h="22px"
                                                />

                                                <IconButton
                                                    aria-label="Remover"
                                                    icon={<AiOutlineDelete />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="textMuted"
                                                    _hover={{ color: 'red.400', bg: 'rgba(229,62,62,0.1)' }}
                                                    onClick={(e) => { e.stopPropagation(); handleRemoverCompromisso(comp.id); }}
                                                    minW="22px" h="22px"
                                                />
                                            </HStack>
                                        </Box>
                                    );
                                })}

                                {/* Formulário inline de novo compromisso */}
                                {novoCompromissoAberto ? (
                                    <Box
                                        bg="surfaceHover"
                                        border="1px solid"
                                        borderColor="secondary"
                                        borderRadius="lg"
                                        p={3}
                                    >
                                        <VStack align="stretch" spacing={2}>
                                            {/* Tipo + Título */}
                                            <HStack spacing={2}>
                                                <SearchableSelect
                                                    size="sm"
                                                    value={novoCompromisso.tipo}
                                                    onChange={v => setNovoCompromisso(p => ({ ...p, tipo: v || TIPOS_COMPROMISSO[0].id }))}
                                                    options={TIPOS_COMPROMISSO.map(t => ({ value: t.id, label: t.id }))}
                                                    placeholder="Tipo"
                                                    bg="surfaceHover"
                                                    borderColor="borderPrimary"
                                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                    maxW="120px"
                                                    fontSize="xs"
                                                    fontWeight="700"
                                                />
                                                <Input
                                                    size="sm"
                                                    value={novoCompromisso.titulo}
                                                    onChange={e => setNovoCompromisso(p => ({ ...p, titulo: e.target.value }))}
                                                    placeholder="Ex: Reunião com Roberto"
                                                    bg="surfaceHover"
                                                    borderColor="borderPrimary"
                                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                    fontWeight="700"
                                                    flex={1}
                                                    autoFocus
                                                />
                                            </HStack>

                                            {/* Data início + Data fim */}
                                            <HStack spacing={2} align="flex-end">
                                                <Box flex={1}>
                                                    <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>Início</Text>
                                                    <Input
                                                        size="sm"
                                                        type="date"
                                                        value={novoCompromisso.data}
                                                        min={new Date().toLocaleDateString('en-CA')}
                                                        onChange={e => setNovoCompromisso(p => ({ ...p, data: e.target.value }))}
                                                        bg="surfaceHover"
                                                        borderColor="borderPrimary"
                                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                        fontSize="xs"
                                                        sx={{ colorScheme: 'dark' }}
                                                    />
                                                </Box>
                                                <Box flex={1}>
                                                    <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>Fim</Text>
                                                    <Input
                                                        size="sm"
                                                        type="date"
                                                        value={novoCompromisso.dataFim}
                                                        min={novoCompromisso.data || new Date().toLocaleDateString('en-CA')}
                                                        onChange={e => setNovoCompromisso(p => ({ ...p, dataFim: e.target.value }))}
                                                        bg="surfaceHover"
                                                        borderColor="borderPrimary"
                                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                        fontSize="xs"
                                                        sx={{ colorScheme: 'dark' }}
                                                    />
                                                </Box>
                                            </HStack>

                                            {/* Hora início + Hora fim */}
                                            <HStack spacing={2} align="flex-end">
                                                <Box flex={1}>
                                                    <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>Hora início</Text>
                                                    <Input
                                                        size="sm"
                                                        type="time"
                                                        value={novoCompromisso.horaInicio}
                                                        onChange={e => setNovoCompromisso(p => ({ ...p, horaInicio: e.target.value }))}
                                                        bg="surfaceHover"
                                                        borderColor="borderPrimary"
                                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                        fontSize="xs"
                                                        sx={{ colorScheme: 'dark' }}
                                                    />
                                                </Box>
                                                <Box flex={1}>
                                                    <Text fontSize="9px" color="textMuted" fontWeight="800" textTransform="uppercase" letterSpacing="0.5px" mb={0.5}>Hora fim</Text>
                                                    <Input
                                                        size="sm"
                                                        type="time"
                                                        value={novoCompromisso.horaFim}
                                                        onChange={e => setNovoCompromisso(p => ({ ...p, horaFim: e.target.value }))}
                                                        bg="surfaceHover"
                                                        borderColor="borderPrimary"
                                                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                        fontSize="xs"
                                                        sx={{ colorScheme: 'dark' }}
                                                    />
                                                </Box>
                                            </HStack>

                                            {/* Local + Responsável */}
                                            <HStack spacing={2}>
                                                <Input
                                                    size="sm"
                                                    value={novoCompromisso.local}
                                                    onChange={e => setNovoCompromisso(p => ({ ...p, local: e.target.value }))}
                                                    placeholder="Local ou link (ex: Sala 1, Google Meet...)"
                                                    bg="surfaceHover"
                                                    borderColor="borderPrimary"
                                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                    fontSize="xs"
                                                    flex={1}
                                                />
                                                <SearchableSelect
                                                    size="sm"
                                                    value={novoCompromisso.employee_id || ''}
                                                    onChange={v => setNovoCompromisso(p => ({ ...p, employee_id: v || '' }))}
                                                    options={employees.map(emp => ({ value: emp.id, label: emp.name }))}
                                                    placeholder="Responsável"
                                                    isClearable
                                                    bg="surfaceHover"
                                                    borderColor="borderPrimary"
                                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                    maxW="160px"
                                                    fontSize="xs"
                                                />
                                            </HStack>

                                            {/* Descrição */}
                                            <Textarea
                                                size="sm"
                                                value={novoCompromisso.descricao}
                                                onChange={e => setNovoCompromisso(p => ({ ...p, descricao: e.target.value }))}
                                                placeholder="Detalhes / pauta (opcional)"
                                                bg="surfaceHover"
                                                borderColor="borderPrimary"
                                                _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                                fontSize="xs"
                                                rows={2}
                                                resize="vertical"
                                            />

                                            {/* Ações */}
                                            <HStack spacing={2} justify="flex-end">
                                                <Button
                                                    size="xs"
                                                    variant="ghost"
                                                    color="textSecondary"
                                                    onClick={() => {
                                                        setNovoCompromissoAberto(false);
                                                        setNovoCompromisso(COMPROMISSO_INICIAL);
                                                        setEditandoCompromissoId(null);
                                                    }}
                                                >
                                                    Cancelar
                                                </Button>
                                                <Button
                                                    size="xs"
                                                    bg="secondary"
                                                    color="primary"
                                                    _hover={{ bg: 'secondaryHover' }}
                                                    fontWeight="800"
                                                    leftIcon={<AiOutlineCheck />}
                                                    onClick={handleAdicionarCompromisso}
                                                >
                                                    {editandoCompromissoId ? 'Salvar' : 'Adicionar'}
                                                </Button>
                                            </HStack>
                                        </VStack>
                                    </Box>
                                ) : (
                                    <HStack spacing={2} flexWrap="wrap">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            color="textMuted"
                                            _hover={{ color: 'secondary', bg: 'surfaceHover' }}
                                            leftIcon={<AiOutlinePlus />}
                                            fontWeight="700"
                                            justifyContent="flex-start"
                                            onClick={() => setNovoCompromissoAberto(true)}
                                        >
                                            Adicionar compromisso
                                        </Button>
                                    </HStack>
                                )}

                                {/* Aviso de integração */}
                                {(formCard.compromissos || []).length > 0 && (
                                    <HStack spacing={1.5} px={1} pt={1}>
                                        <Icon as={AiOutlineInfoCircle} color="textMuted" boxSize="11px" />
                                        <Text fontSize="10px" color="textMuted">
                                            Compromissos aparecem automaticamente no <strong>Calendário</strong>
                                        </Text>
                                    </HStack>
                                )}
                            </VStack>

                            {/* -- Seção: Observações -- */}
                            <SecaoLabel label="Observações" />
                            <AnotacaoFormSection
                                isEditing={!!cardEditando}
                                existingValue={formCard.observacoes}
                                newValue={novaAnotacao}
                                onNewValueChange={setNovaAnotacao}
                                entityLabel="negócio"
                                showSaveShortcut
                            />

                        </VStack>
                    </ModalBody>

                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button size="sm" variant="ghost" color="textSecondary" _hover={{ bg: 'surfaceHover' }} onClick={handleFecharCard}>
                            Cancelar
                        </Button>
                        <Button size="sm" bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSalvarCard}>
                            {cardEditando ? 'Salvar Alterações' : 'Criar Negócio'}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ------------------------------------------------------?
                Modal: Visualizar Negócio (read-only)
            ----------------------------------------------------?-- */}
            <Modal isOpen={isViewOpen} onClose={handleFecharVisualizar} isCentered size="2xl" scrollBehavior="inside">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" overflow="hidden">
                    {cardVisualizando && (() => {
                        const colunaCard = pipelineAtivo?.colunas.find(c => c.id === cardVisualizando.colunaId);
                        const marcaCard = cardVisualizando.marcaId ? (marcas || []).find(m => m.id === cardVisualizando.marcaId) : null;
                        const contatoCard = contatos.find(c =>
                            (cardVisualizando.contato && c.nome === cardVisualizando.contato) ||
                            (cardVisualizando.empresa && c.empresa === cardVisualizando.empresa)
                        ) || null;
                        const prioridadeMeta = PRIORIDADES.find(p => p.id === cardVisualizando.prioridade) || PRIORIDADES[1];
                        const diasAte = cardVisualizando.dataFechamento
                            ? Math.ceil((new Date(cardVisualizando.dataFechamento) - new Date()) / (1000 * 60 * 60 * 24))
                            : null;
                        const atrasado = diasAte !== null && diasAte < 0;
                        const corProb = cardVisualizando.probabilidade >= 70 ? '#02EA75' : cardVisualizando.probabilidade >= 40 ? '#ECC94B' : '#FC8181';

                        return (
                            <>
                                {/* Faixa colorida superior (cor da coluna) */}
                                <Box h="6px" bg={colunaCard?.cor || '#4299E1'} />

                                <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" pb={4}>
                                    <HStack spacing={3} align="flex-start">
                                        <Box flex={1} minW={0}>
                                            <HStack spacing={2} mb={1}>
                                                {colunaCard && (
                                                    <HStack spacing={1.5}>
                                                        <Box w="8px" h="8px" borderRadius="full" bg={colunaCard.cor} />
                                                        <Text fontSize="10px" color="textSecondary" fontWeight="800" textTransform="uppercase" letterSpacing="1.5px">
                                                            {colunaCard.label}
                                                        </Text>
                                                    </HStack>
                                                )}
                                                <Badge
                                                    fontSize="9px"
                                                    bg={`${prioridadeMeta.cor}20`}
                                                    color={prioridadeMeta.cor}
                                                    fontWeight="900"
                                                    px={1.5}
                                                    borderRadius="sm"
                                                    textTransform="uppercase"
                                                >
                                                    {cardVisualizando.prioridade}
                                                </Badge>
                                                {cardVisualizando.canceled && (
                                                    <Badge
                                                        fontSize="9px"
                                                        bg="rgba(252,129,129,0.18)"
                                                        color="#FC8181"
                                                        fontWeight="900"
                                                        px={1.5}
                                                        borderRadius="sm"
                                                        textTransform="uppercase"
                                                    >
                                                        Cancelado
                                                    </Badge>
                                                )}
                                            </HStack>
                                            <Text fontWeight="900" fontSize="lg" color="textPrimary" lineHeight="1.2" textDecoration={cardVisualizando.canceled ? 'line-through' : 'none'} opacity={cardVisualizando.canceled ? 0.7 : 1}>
                                                {cardVisualizando.titulo}
                                            </Text>
                                        </Box>
                                    </HStack>
                                </ModalHeader>
                                <ModalCloseButton color="textSecondary" top={5} />

                                <ModalBody py={5}>
                                    <VStack spacing={4} align="stretch">
                                        {/* Card destaque: Valor + Probabilidade */}
                                        <SimpleGrid columns={2} spacing={3}>
                                            <Box bg="surfaceHover" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={4}>
                                                <Text fontSize="9px" color="textMuted" fontWeight="900" textTransform="uppercase" letterSpacing="1.5px" mb={1}>
                                                    Valor
                                                </Text>
                                                <Text fontSize="2xl" color="secondary" fontWeight="900" lineHeight="1">
                                                    {fmt(cardVisualizando.valor)}
                                                </Text>
                                            </Box>
                                            <Box bg="surfaceHover" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={4}>
                                                <Text fontSize="9px" color="textMuted" fontWeight="900" textTransform="uppercase" letterSpacing="1.5px" mb={1}>
                                                    Probabilidade
                                                </Text>
                                                <HStack align="baseline" spacing={1}>
                                                    <Text fontSize="2xl" color={corProb} fontWeight="900" lineHeight="1">
                                                        {cardVisualizando.probabilidade || 0}
                                                    </Text>
                                                    <Text fontSize="md" color={corProb} fontWeight="800">%</Text>
                                                </HStack>
                                                <Box mt={2} h="3px" bg="gray.800" borderRadius="full" overflow="hidden">
                                                    <Box w={`${cardVisualizando.probabilidade || 0}%`} h="full" bg={corProb} />
                                                </Box>
                                            </Box>
                                        </SimpleGrid>

                                        {/* Cliente: Marca ERP / Contato CRM / Novo */}
                                        {marcaCard && (
                                            <Box bg="#4299E110" border="1px solid" borderColor="#4299E140" borderRadius="xl" p={3}>
                                                <HStack spacing={3} align="flex-start">
                                                    <Avatar
                                                        size="md"
                                                        name={marcaCard.nome}
                                                        src={marcaCard.logo}
                                                        bg={marcaCard.cor_principal || 'gray.600'}
                                                        color="textPrimary"
                                                        fontWeight="900"
                                                        sx={{ '& img': { objectFit: 'contain', p: '4px' } }}
                                                    />
                                                    <Box flex={1} minW={0}>
                                                        <HStack spacing={2}>
                                                            <Text fontSize="sm" color="textPrimary" fontWeight="900">{marcaCard.nome}</Text>
                                                            <Badge fontSize="8px" bg="#4299E120" color="#4299E1" fontWeight="900" px={1.5}>ERP</Badge>
                                                        </HStack>
                                                        <Text fontSize="xs" color="textSecondary" mt={0.5}>
                                                            {marcaCard.provedor || 'Sem ERP definido'}
                                                        </Text>
                                                    </Box>
                                                </HStack>
                                            </Box>
                                        )}

                                        {contatoCard && (
                                            <Box bg="surfaceHover" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={3} mt={marcaCard ? 3 : 0}>
                                                <HStack spacing={3} align="flex-start">
                                                    <Avatar size="md" name={contatoCard.nome} bg="secondary" color="primary" fontWeight="900" />
                                                    <Box flex={1} minW={0}>
                                                        <HStack spacing={2}>
                                                            <Text fontSize="sm" color="textPrimary" fontWeight="900">{contatoCard.nome}</Text>
                                                            <Badge fontSize="8px" bg="#02EA7520" color="secondary" fontWeight="900" px={1.5}>CRM</Badge>
                                                        </HStack>
                                                        <Text fontSize="xs" color="textSecondary" mt={0.5}>
                                                            {contatoCard.cargo ? `${contatoCard.cargo} • ` : ''}{contatoCard.empresa || cardVisualizando.empresa}
                                                        </Text>
                                                        <HStack spacing={4} mt={2} flexWrap="wrap">
                                                            {contatoCard.telefone && (
                                                                <HStack spacing={1.5}>
                                                                    <Icon as={AiOutlinePhone} color="textMuted" boxSize="11px" />
                                                                    <Text fontSize="xs" color="textSecondary">{contatoCard.telefone}</Text>
                                                                </HStack>
                                                            )}
                                                            {contatoCard.email && (
                                                                <HStack spacing={1.5}>
                                                                    <Icon as={AiOutlineMail} color="textMuted" boxSize="11px" />
                                                                    <Text fontSize="xs" color="textSecondary">{contatoCard.email}</Text>
                                                                </HStack>
                                                            )}
                                                        </HStack>
                                                    </Box>
                                                </HStack>
                                            </Box>
                                        )}

                                        {!marcaCard && !contatoCard && cardVisualizando.empresa && (
                                            <Box bg={cardVisualizando.clienteNovo ? '#ED893610' : 'surfaceRaised'} border="1px solid" borderColor={cardVisualizando.clienteNovo ? '#ED893640' : 'borderPrimary'} borderRadius="xl" p={3}>
                                                <HStack spacing={3}>
                                                    <Flex w="40px" h="40px" bg={cardVisualizando.clienteNovo ? '#ED893620' : 'surfaceRaised'} borderRadius="lg" align="center" justify="center" flexShrink={0}>
                                                        <Icon as={cardVisualizando.clienteNovo ? AiOutlineUserAdd : AiOutlineBank} color={cardVisualizando.clienteNovo ? '#ED8936' : 'gray.400'} boxSize="18px" />
                                                    </Flex>
                                                    <Box flex={1}>
                                                        <HStack spacing={2}>
                                                            <Text fontSize="sm" color="textPrimary" fontWeight="900">{cardVisualizando.empresa}</Text>
                                                            {cardVisualizando.clienteNovo && (
                                                                <Badge fontSize="8px" bg="#ED893620" color="#ED8936" fontWeight="900" px={1.5}>Novo</Badge>
                                                            )}
                                                        </HStack>
                                                        {cardVisualizando.contato && (
                                                            <Text fontSize="xs" color="textSecondary" mt={0.5}>{cardVisualizando.contato}</Text>
                                                        )}
                                                    </Box>
                                                </HStack>
                                            </Box>
                                        )}

                                        {/* Detalhes em grid */}
                                        <SimpleGrid columns={2} spacing={3}>
                                            <DetailItem icon={AiOutlineCalendar} label="Início" value={
                                                cardVisualizando.dataInicio
                                                    ? new Date(cardVisualizando.dataInicio + 'T12:00:00').toLocaleDateString('pt-BR')
                                                    : '—'
                                            } />
                                            <DetailItem icon={AiOutlineCalendar} label="Fechamento" value={
                                                cardVisualizando.dataFechamento
                                                    ? `${new Date(cardVisualizando.dataFechamento + 'T12:00:00').toLocaleDateString('pt-BR')}${atrasado ? ' • atrasado' : diasAte !== null ? ` • ${diasAte}d` : ''}`
                                                    : '—'
                                            } valueColor={atrasado ? 'red.400' : 'textPrimary'} />
                                            <DetailItem icon={AiOutlineFire} label="Canal" value={cardVisualizando.canal || '—'} />
                                            <DetailItem icon={AiOutlineIdcard} label="Responsável" value={cardVisualizando.responsavel || '—'} />
                                            <DetailItem icon={AiOutlinePartition} label="Etapa" value={colunaCard?.label || '—'} valueColor={colunaCard?.cor} />
                                        </SimpleGrid>

                                        {/* Compromissos / Agenda */}
                                        {(cardVisualizando.compromissos || []).length > 0 && (() => {
                                            const hoje = new Date();
                                            hoje.setHours(0, 0, 0, 0);
                                            const compsOrdenados = [...cardVisualizando.compromissos].sort((a, b) => {
                                                // Não-concluídos primeiro
                                                if (a.concluido !== b.concluido) return a.concluido ? 1 : -1;
                                                // Depois por data ascendente
                                                return new Date(a.data) - new Date(b.data);
                                            });
                                            const proximo = compsOrdenados.find(c => !c.concluido && new Date(c.data) >= hoje);
                                            return (
                                                <Box bg="surfaceHover" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={3}>
                                                    <HStack justify="space-between" mb={3}>
                                                        <HStack spacing={1.5}>
                                                            <Icon as={AiOutlineCalendar} color="secondary" boxSize="11px" />
                                                            <Text fontSize="9px" color="textMuted" fontWeight="900" textTransform="uppercase" letterSpacing="1.5px">
                                                                Agenda • {cardVisualizando.compromissos.length}
                                                            </Text>
                                                        </HStack>
                                                        {proximo && (
                                                            <Badge fontSize="8px" bg="secondary" color="primary" fontWeight="900" px={1.5} borderRadius="sm" textTransform="uppercase">
                                                                Próximo: {new Date(proximo.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                                            </Badge>
                                                        )}
                                                    </HStack>

                                                    <VStack align="stretch" spacing={2}>
                                                        {compsOrdenados.map(comp => {
                                                            const tipoMeta = TIPOS_COMPROMISSO.find(t => t.id === comp.tipo) || TIPOS_COMPROMISSO[0];
                                                            const dataComp = new Date(comp.data + 'T12:00:00');
                                                            const diasAteComp = Math.ceil((dataComp - hoje) / (1000 * 60 * 60 * 24));
                                                            const isPast = diasAteComp < 0 && !comp.concluido;
                                                            return (
                                                                <HStack
                                                                    key={comp.id}
                                                                    bg="background"
                                                                    border="1px solid"
                                                                    borderColor={comp.concluido ? 'gray.700' : isPast ? 'red.400' : 'gray.700'}
                                                                    borderLeft="3px solid"
                                                                    borderLeftColor={tipoMeta.cor}
                                                                    borderRadius="md"
                                                                    p={2.5}
                                                                    spacing={2.5}
                                                                    opacity={comp.concluido ? 0.5 : 1}
                                                                >
                                                                    {/* Bloco da data (estilo "post-it") */}
                                                                    <Box
                                                                        bg={`${tipoMeta.cor}15`}
                                                                        borderRadius="md"
                                                                        px={2}
                                                                        py={1}
                                                                        minW="42px"
                                                                        textAlign="center"
                                                                        flexShrink={0}
                                                                    >
                                                                        <Text fontSize="9px" color={tipoMeta.cor} fontWeight="900" textTransform="uppercase" lineHeight="1">
                                                                            {dataComp.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '')}
                                                                        </Text>
                                                                        <Text fontSize="lg" color="textPrimary" fontWeight="900" lineHeight="1">
                                                                            {dataComp.getDate()}
                                                                        </Text>
                                                                    </Box>

                                                                    <Box flex={1} minW={0}>
                                                                        <HStack spacing={1.5} mb={0.5}>
                                                                            <Badge fontSize="8px" bg={`${tipoMeta.cor}20`} color={tipoMeta.cor} fontWeight="900" px={1.5} borderRadius="sm">
                                                                                {comp.tipo}
                                                                            </Badge>
                                                                            {comp.concluido && (
                                                                                <Badge fontSize="8px" bg="surfaceHover" color="textMuted" fontWeight="900" px={1.5}>
                                                                                    CONCLUÍDO
                                                                                </Badge>
                                                                            )}
                                                                            {isPast && !comp.concluido && (
                                                                                <Badge fontSize="8px" bg="red.400" color="textPrimary" fontWeight="900" px={1.5}>
                                                                                    ATRASADO
                                                                                </Badge>
                                                                            )}
                                                                        </HStack>
                                                                        <Text fontSize="sm" color="textPrimary" fontWeight="800" lineHeight="1.2" textDecoration={comp.concluido ? 'line-through' : 'none'}>
                                                                            {comp.titulo}
                                                                        </Text>
                                                                        <HStack spacing={2} fontSize="10px" color="textMuted" mt={0.5} flexWrap="wrap">
                                                                            {(comp.horaInicio || comp.hora) && (
                                                                                <Text fontWeight="700">{comp.horaInicio || comp.hora}{comp.horaFim ? `?${comp.horaFim}` : ''}</Text>
                                                                            )}
                                                                            {comp.local && (
                                                                                <Text isTruncated>? {comp.local}</Text>
                                                                            )}
                                                                        </HStack>
                                                                        {comp.descricao && (
                                                                            <Text fontSize="11px" color="textSecondary" mt={1} noOfLines={2}>
                                                                                {comp.descricao}
                                                                            </Text>
                                                                        )}
                                                                    </Box>
                                                                </HStack>
                                                            );
                                                        })}
                                                    </VStack>
                                                </Box>
                                            );
                                        })()}

                                        {/* -- Atividades do negócio -- */}
                                        {(() => {
                                            const atvsCard = (atividades || []).filter(a => String(a.cardId) === String(cardVisualizando.id));
                                            const pendentes = atvsCard.filter(a => !a.completed).sort((a, b) => (a.dataInicio || '').localeCompare(b.dataInicio || ''));
                                            const proxima = pendentes[0];
                                            const fmtD = (d) => d ? d.split('-').reverse().join('/') : '';
                                            return (
                                                <Box bg="surfaceHover" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={3}>
                                                    <HStack justify="space-between" mb={2}>
                                                        <HStack spacing={1.5}>
                                                            <Icon as={AiOutlineCalendar} color="secondary" boxSize="12px" />
                                                            <Text fontSize="9px" color="textMuted" fontWeight="900" textTransform="uppercase" letterSpacing="1.5px">
                                                                Atividades ({atvsCard.length})
                                                            </Text>
                                                        </HStack>
                                                        <Button size="xs" variant="ghost" color="secondary" leftIcon={<AiOutlinePlus />} _hover={{ bg: 'secondarySubtle' }}
                                                            onClick={() => navigate(`/comercial/atividades?card=${cardVisualizando.id}`)}>
                                                            Nova atividade
                                                        </Button>
                                                    </HStack>
                                                    {atvsCard.length === 0 ? (
                                                        <Text fontSize="xs" color="textSecondary">Nenhuma atividade vinculada a este negócio.</Text>
                                                    ) : (
                                                        <VStack align="stretch" spacing={1.5}>
                                                            {proxima && (
                                                                <HStack spacing={2} bg="secondarySubtle" borderRadius="md" px={2} py={1.5}>
                                                                    <Icon as={AiOutlineFire} color="secondary" boxSize="12px" />
                                                                    <Text fontSize="xs" color="textPrimary" fontWeight="700" noOfLines={1} flex={1}>Próxima: {proxima.nome}</Text>
                                                                    <Text fontSize="10px" color="secondary" fontWeight="800">{fmtD(proxima.dataInicio)}</Text>
                                                                </HStack>
                                                            )}
                                                            {atvsCard.map(a => (
                                                                <HStack key={a.id} spacing={2} justify="space-between">
                                                                    <HStack spacing={2} minW={0}>
                                                                        <Icon as={AiOutlineCheckCircle} boxSize="12px" color={a.completed ? 'secondary' : 'gray.600'} />
                                                                        <Text fontSize="xs" color={a.completed ? 'textSecondary' : 'textPrimary'} textDecoration={a.completed ? 'line-through' : 'none'} noOfLines={1}>{a.nome}</Text>
                                                                        {a.tipo && <Badge fontSize="8px" bg="surfaceRaised" color="textSecondary" borderRadius="sm">{a.tipo}</Badge>}
                                                                    </HStack>
                                                                    <Text fontSize="10px" color="textMuted" flexShrink={0}>{fmtD(a.dataInicio)}</Text>
                                                                </HStack>
                                                            ))}
                                                        </VStack>
                                                    )}
                                                </Box>
                                            );
                                        })()}

                                        {/* Observações */}
                                        {cardVisualizando.observacoes?.trim() && (
                                            <Box bg="surfaceHover" border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={3}>
                                                <Text fontSize="9px" color="textMuted" fontWeight="900" textTransform="uppercase" letterSpacing="1.5px" mb={2}>
                                                    Observações
                                                </Text>
                                                <Text fontSize="sm" color="textSecondary" whiteSpace="pre-wrap" lineHeight="1.6">
                                                    {cardVisualizando.observacoes}
                                                </Text>
                                            </Box>
                                        )}

                                        {/* Metadados (criação) */}
                                        {cardVisualizando.criadoEm && (
                                            <Text fontSize="10px" color="textMuted" textAlign="center" fontWeight="700">
                                                Criado em {new Date(cardVisualizando.criadoEm).toLocaleString('pt-BR')}
                                            </Text>
                                        )}
                                    </VStack>
                                </ModalBody>

                                <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={2} rowGap={2} flexWrap="wrap" justifyContent="space-between">
                                    <Button
                                        size="sm"
                                        leftIcon={<AiOutlineDelete />}
                                        variant="ghost"
                                        color="red.400"
                                        _hover={{ bg: 'rgba(229,62,62,0.1)' }}
                                        fontWeight="800"
                                        flexShrink={0}
                                        onClick={() => handleAbrirExcluir(cardVisualizando)}
                                    >
                                        Excluir
                                    </Button>
                                    <HStack spacing={2} rowGap={2} flexWrap="wrap" justify="flex-end">
                                        <Button size="sm" variant="ghost" color="textSecondary" _hover={{ bg: 'surfaceHover' }} flexShrink={0} onClick={handleFecharVisualizar}>
                                            Fechar
                                        </Button>
                                        {cardVisualizando.canceled ? (
                                            <Button
                                                size="sm"
                                                leftIcon={<AiOutlineUndo />}
                                                variant="outline"
                                                borderColor="rgba(236,201,75,0.45)"
                                                color="#ECC94B"
                                                _hover={{ bg: 'rgba(236,201,75,0.12)' }}
                                                fontWeight="800"
                                                flexShrink={0}
                                                onClick={() => setAcaoConfirmar({ tipo: 'reativar', card: cardVisualizando, fecharFicha: true })}
                                            >
                                                Reativar
                                            </Button>
                                        ) : (
                                            <Button
                                                size="sm"
                                                leftIcon={<AiOutlineCloseCircle />}
                                                variant="outline"
                                                borderColor="rgba(252,129,129,0.45)"
                                                color="#FC8181"
                                                _hover={{ bg: 'rgba(252,129,129,0.12)' }}
                                                fontWeight="800"
                                                flexShrink={0}
                                                onClick={() => setAcaoConfirmar({ tipo: 'cancelar', card: cardVisualizando, fecharFicha: true })}
                                            >
                                                Cancelar
                                            </Button>
                                        )}
                                        {!cardVisualizando.canceled && (
                                            <Button
                                                size="sm"
                                                leftIcon={<AiOutlineCheckCircle />}
                                                variant="outline"
                                                borderColor="rgba(1,233,117,0.45)"
                                                color="secondary"
                                                _hover={{ bg: 'secondarySubtle' }}
                                                fontWeight="800"
                                                flexShrink={0}
                                                onClick={async () => {
                                                    const colunas = pipelineAtivo?.colunas || [];
                                                    const colGanho = colunas.find(col => String(col.label || col.nome || '').toLowerCase().includes('ganho'));
                                                    const colPerdido = colunas.find(col => String(col.label || col.nome || '').toLowerCase().includes('perdido'));

                                                    const isCurrentGanhoOrPerdido = String(cardVisualizando.colunaId) === String(colGanho?.id) || String(cardVisualizando.colunaId) === String(colPerdido?.id);

                                                    if (isCurrentGanhoOrPerdido) {
                                                        // Confirmação padrão antes de concluir (evita clique acidental)
                                                        setAcaoConfirmar({ tipo: 'concluir', card: cardVisualizando, fecharFicha: true });
                                                    } else {
                                                        setCardConcluindo(cardVisualizando);
                                                        onConcluirOpen();
                                                    }
                                                }}
                                            >
                                                Concluir
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            leftIcon={<AiOutlineEdit />}
                                            bg="secondary"
                                            color="primary"
                                            _hover={{ bg: 'secondaryHover' }}
                                            fontWeight="800"
                                            flexShrink={0}
                                            onClick={() => {
                                                handleFecharVisualizar();
                                                setTimeout(() => handleAbrirCard(cardVisualizando, cardVisualizando.colunaId), 100);
                                            }}
                                        >
                                            Editar
                                        </Button>
                                    </HStack>
                                </ModalFooter>
                            </>
                        );
                    })()}
                </ModalContent>
            </Modal>

            {/* ------------------------------------------------------?
                Confirmação padrão de exclusão
            ----------------------------------------------------?-- */}
            <ConfirmDeleteDialog
                isOpen={isDelOpen}
                onClose={onDelClose}
                onConfirm={handleConfirmarExcluir}
                title="Excluir Negócio"
                itemName={cardExcluindo?.titulo}
                warning={cardExcluindo && (atividades || []).some(a => String(a.cardId) === String(cardExcluindo.id))}
                description={(cardExcluindo && (atividades || []).some(a => String(a.cardId) === String(cardExcluindo.id)))
                    ? "Atenção: Este negócio possui atividades vinculadas. Se você prosseguir com a exclusão, essas atividades perderão o vínculo com este negócio."
                    : (cardExcluindo?.empresa ? `${cardExcluindo.empresa}${cardExcluindo.valor ? ` • ${fmt(cardExcluindo.valor)}` : ''}` : '')}
            />

            <ConfirmDeleteDialog
                isOpen={isDelColunaOpen}
                onClose={onDelColunaClose}
                onConfirm={handleConfirmarExcluirColuna}
                title="Excluir Coluna"
                itemName={colunaExcluindo?.label}
                description="Os negócios desta coluna também serão removidos do board."
            />

            <ConfirmDeleteDialog
                isOpen={isDelPipelineOpen}
                onClose={onDelPipelineClose}
                onConfirm={handleConfirmarExcluirPipeline}
                title="Excluir Pipeline"
                itemName={pipelineExcluindo?.nome}
                description={`Os negocios e atividades vinculadas nao serao apagados, mas ficarao sem funil. Impacto atual: ${pipelineDeleteImpact.negocios} negocio(s) e ${pipelineDeleteImpact.atividades} atividade(s) vinculada(s).`}
            />

            {/* Confirmação padrão de ação (cancelar/reativar/concluir) — evita cliques acidentais */}
            <ConfirmActionDialog
                isOpen={!!acaoConfirmar}
                onClose={() => setAcaoConfirmar(null)}
                onConfirm={executarAcaoConfirmada}
                title={acaoConfirmar?.tipo === 'cancelar' ? 'Cancelar Negócio' : acaoConfirmar?.tipo === 'reativar' ? 'Reativar Negócio' : 'Concluir Negócio'}
                itemName={acaoConfirmar?.card?.titulo}
                description={acaoConfirmar?.tipo === 'cancelar'
                    ? 'O negócio ficará marcado como CANCELADO e sai dos KPIs de abertos. É possível reativá-lo depois.'
                    : acaoConfirmar?.tipo === 'reativar'
                        ? 'O negócio volta a contar como em andamento.'
                        : 'O negócio será marcado como CONCLUÍDO e vai para a tela Relatórios.'}
                confirmLabel={acaoConfirmar?.tipo === 'cancelar' ? 'Sim, cancelar' : acaoConfirmar?.tipo === 'reativar' ? 'Sim, reativar' : 'Sim, concluir'}
                scheme={acaoConfirmar?.tipo === 'cancelar' ? 'red' : acaoConfirmar?.tipo === 'reativar' ? 'amber' : 'green'}
                icon={acaoConfirmar?.tipo === 'cancelar' ? AiOutlineCloseCircle : acaoConfirmar?.tipo === 'reativar' ? AiOutlineUndo : AiOutlineCheckCircle}
            />

            {/* AlertDialog: Confirmar criação com empresa nova/externa */}
            <AlertDialog isOpen={isConfirmEmpresaOpen} leastDestructiveRef={confirmEmpresaRef} onClose={onConfirmEmpresaClose} isCentered motionPreset="slideInBottom">
                <AlertDialogOverlay backdropFilter="blur(4px)">
                    <AlertDialogContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="xl">
                        <AlertDialogHeader fontSize="lg" fontWeight="900" borderBottom="1px solid" borderColor="borderPrimary">Empresa não cadastrada</AlertDialogHeader>
                        <AlertDialogBody py={5}>
                            A empresa <Text as="span" fontWeight="800" color="textPrimary">{formCard.empresa || '—'}</Text> não está cadastrada como marca no ERP. Tem certeza que deseja criar o negócio com uma empresa nova/externa?
                        </AlertDialogBody>
                        <AlertDialogFooter gap={2} borderTop="1px solid" borderColor="borderPrimary">
                            <Button ref={confirmEmpresaRef} variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={onConfirmEmpresaClose}>Cancelar</Button>
                            <Button bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={() => { onConfirmEmpresaClose(); handleSalvarCard(true); }}>Sim, criar</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            {/* Modal: Confirmar Conclusão (Ganho/Perdido) */}
            <Modal isOpen={isConcluirOpen} onClose={onConcluirClose} isCentered size="sm">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="xl" overflow="hidden">
                    <Box h="4px" bg="secondary" />
                    <ModalHeader pb={2}>
                        <HStack spacing={3}>
                            <Flex w="40px" h="40px" bg="rgba(2,234,117,0.15)" borderRadius="full" align="center" justify="center" flexShrink={0}>
                                <Icon as={AiOutlineCheckCircle} color="secondary" boxSize="20px" />
                            </Flex>
                            <Box>
                                <Text fontSize="md" fontWeight="950" lineHeight="1.2" color="textPrimary">
                                    Concluir Negócio
                                </Text>
                                <Text fontSize="xs" color="textSecondary" fontWeight="600" mt={0.5}>
                                    Selecione o resultado da negociação
                                </Text>
                            </Box>
                        </HStack>
                    </ModalHeader>
                    <ModalBody py={4}>
                        {cardConcluindo && (
                            <VStack align="stretch" spacing={3}>
                                <Box bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"} border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={3}>
                                    <Text fontSize="xs" color="textSecondary" fontWeight="700" mb={1}>
                                        Negócio/Proposta:
                                    </Text>
                                    <Text fontSize="md" color="textPrimary" fontWeight="900" lineHeight="1.2">
                                        {cardConcluindo.titulo}
                                    </Text>
                                    {cardConcluindo.empresa && (
                                        <Text fontSize="xs" color="textSecondary" mt={1}>
                                            {cardConcluindo.empresa}{cardConcluindo.valor ? ` • ${fmt(cardConcluindo.valor)}` : ''}
                                        </Text>
                                    )}
                                </Box>

                                <HStack spacing={3} wrap="wrap">
                                    {resultadosNegocio && resultadosNegocio.length > 0 ? (
                                        resultadosNegocio.map(res => {
                                            // Visual pelo PAPEL (1 positivo/verde · 2 negativo/vermelho ·
                                            // 0 neutro/cinza); a cor própria da opção tem prioridade.
                                            const papel = Number(res.role);
                                            let bg = "gray.600", color = "textPrimary", hoverBg = "gray.700";
                                            if (papel === 1) { bg = "secondary"; color = "primary"; hoverBg = "secondaryHover"; }
                                            else if (papel === 2) { bg = "red.500"; color = "textPrimary"; hoverBg = "red.600"; }
                                            return (
                                                <Button
                                                    key={res.id}
                                                    flex={1}
                                                    minW="100px"
                                                    bg={res.cor || bg}
                                                    color={color}
                                                    _hover={{ bg: res.cor || hoverBg, opacity: res.cor ? 0.85 : 1 }}
                                                    fontWeight="900"
                                                    size="md"
                                                    borderRadius="xl"
                                                    onClick={() => handleConfirmarConclusao(res.nome, res.id, papel)}
                                                >
                                                    {res.nome.toUpperCase()}
                                                </Button>
                                            );
                                        })
                                    ) : (
                                        <>
                                            <Button
                                                flex={1}
                                                bg="secondary"
                                                color="primary"
                                                _hover={{ bg: 'secondaryHover' }}
                                                fontWeight="900"
                                                size="md"
                                                borderRadius="xl"
                                                onClick={() => handleConfirmarConclusao('ganho')}
                                            >
                                                GANHO
                                            </Button>
                                            <Button
                                                flex={1}
                                                bg="red.500"
                                                color="textPrimary"
                                                _hover={{ bg: 'red.600' }}
                                                fontWeight="900"
                                                size="md"
                                                borderRadius="xl"
                                                onClick={() => handleConfirmarConclusao('perdido')}
                                            >
                                                PERDIDO
                                            </Button>
                                        </>
                                    )}
                                </HStack>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" py={3}>
                        <Button variant="ghost" size="sm" w="100%" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={onConcluirClose}>
                            Cancelar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal: Nova Coluna */}
            <Modal isOpen={isColunaOpen} onClose={onColunaClose} isCentered size="sm">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary">
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" fontSize="md" fontWeight="800">
                        Nova Coluna
                    </ModalHeader>
                    <ModalCloseButton color="textSecondary" />
                    <ModalBody py={5}>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Nome da Coluna
                                </FormLabel>
                                <Input
                                    value={formColuna.label}
                                    onChange={e => setFormColuna(p => ({ ...p, label: e.target.value }))}
                                    placeholder="Ex: Aguardando Proposta"
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    autoFocus
                                    onKeyDown={e => e.key === 'Enter' && handleSalvarColuna()}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Cor
                                </FormLabel>
                                <Box px={1} pt={1} bg={colorMode === 'dark' ? "whiteAlpha.30" : "blackAlpha.30"} border="1px solid" borderColor={formColuna.cor} borderRadius="lg" p={3}>
                                    <HStack spacing={2} mb={3}>
                                        <Box w="16px" h="16px" borderRadius="full" bg={formColuna.cor} />
                                        <Text fontSize="xs" color="textPrimary" fontWeight="700">{formColuna.cor}</Text>
                                    </HStack>
                                    <ColorPicker value={formColuna.cor} onChange={cor => setFormColuna(p => ({ ...p, cor }))} />
                                </Box>
                            </FormControl>

                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button size="sm" variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={onColunaClose}>
                            Cancelar
                        </Button>
                        <Button size="sm" bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSalvarColuna}>
                            Criar Coluna
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal: Editar Coluna */}
            <Modal isOpen={isRenomearOpen} onClose={onRenomearClose} isCentered size="sm">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary">
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" fontSize="md" fontWeight="800">
                        Editar Coluna
                    </ModalHeader>
                    <ModalCloseButton color="textSecondary" />
                    <ModalBody py={5}>
                        <VStack spacing={4} align="stretch">
                            <FormControl>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Nome
                                </FormLabel>
                                <Input
                                    value={formRenomear.label}
                                    onChange={e => setFormRenomear(p => ({ ...p, label: e.target.value }))}
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    autoFocus
                                    onKeyDown={e => e.key === 'Enter' && handleSalvarRenomear()}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Cor
                                </FormLabel>
                                <Box bg={colorMode === 'dark' ? "whiteAlpha.30" : "blackAlpha.30"} border="1px solid" borderColor={formRenomear.cor} borderRadius="lg" p={3}>
                                    <HStack spacing={2} mb={3}>
                                        <Box w="16px" h="16px" borderRadius="full" bg={formRenomear.cor} />
                                        <Text fontSize="xs" color="textPrimary" fontWeight="700">{formRenomear.cor}</Text>
                                    </HStack>
                                    <ColorPicker value={formRenomear.cor} onChange={cor => setFormRenomear(p => ({ ...p, cor }))} />
                                </Box>
                            </FormControl>

                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button size="sm" variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={onRenomearClose}>
                            Cancelar
                        </Button>
                        <Button size="sm" bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSalvarRenomear}>
                            Salvar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal: Nova Aba */}
            <Modal isOpen={isAbaOpen} onClose={onAbaClose} isCentered size="sm">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary">
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" fontSize="md" fontWeight="800">
                        Novo Pipeline
                    </ModalHeader>
                    <ModalCloseButton color="textSecondary" />
                    <ModalBody py={5}>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Nome do Pipeline
                                </FormLabel>
                                <Input
                                    value={formAba.nome}
                                    onChange={e => setFormAba(p => ({ ...p, nome: e.target.value }))}
                                    placeholder="Ex: ISPs Premium, Grandes Contas..."
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    autoFocus
                                    onKeyDown={e => e.key === 'Enter' && handleSalvarAba()}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Responsavel do Funil
                                </FormLabel>
                                <SearchableSelect
                                    value={formAba.responsavelId}
                                    onChange={v => setFormAba(p => ({ ...p, responsavelId: v || '' }))}
                                    options={(employees || [])
                                        .filter(emp => emp.isWhitelisted !== false && emp.isWhitelisted !== 0)
                                        .map(emp => ({ value: emp.id, label: emp.name }))}
                                    placeholder="Sem responsavel"
                                    isClearable
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Cor da Aba
                                </FormLabel>
                                <Box bg={colorMode === 'dark' ? "whiteAlpha.30" : "blackAlpha.30"} border="1px solid" borderColor={formAba.cor} borderRadius="lg" p={3}>
                                    <HStack spacing={2} mb={3}>
                                        <Box w="16px" h="16px" borderRadius="full" bg={formAba.cor} />
                                        <Text fontSize="xs" color="textPrimary" fontWeight="700">{formAba.cor}</Text>
                                    </HStack>
                                    <ColorPicker value={formAba.cor} onChange={cor => setFormAba(p => ({ ...p, cor }))} />
                                </Box>
                            </FormControl>
                            <Box bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"} p={3} borderRadius="md" border="1px solid" borderColor="borderPrimary">
                                <Text fontSize="xs" color="textSecondary">

                                    Você pode adicionar e personalizar colunas depois.
                                </Text>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button size="sm" variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={onAbaClose}>
                            Cancelar
                        </Button>
                        <Button size="sm" bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSalvarAba}>
                            Criar Pipeline
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal: Renomear Aba */}
            <Modal isOpen={isRenomearAbaOpen} onClose={onRenomearAbaClose} isCentered size="sm">
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary">
                    <ModalHeader borderBottom="1px solid" borderColor="borderPrimary" fontSize="md" fontWeight="800">
                        Editar Pipeline
                    </ModalHeader>
                    <ModalCloseButton color="textSecondary" />
                    <ModalBody py={5}>
                        <VStack spacing={4} align="stretch">
                            <FormControl>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Nome
                                </FormLabel>
                                <Input
                                    value={formRenomearAba.nome}
                                    onChange={e => setFormRenomearAba(p => ({ ...p, nome: e.target.value }))}
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                    autoFocus
                                    onKeyDown={e => e.key === 'Enter' && handleSalvarRenomearAba()}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Responsavel do Funil
                                </FormLabel>
                                <SearchableSelect
                                    value={formRenomearAba.responsavelId}
                                    onChange={v => setFormRenomearAba(p => ({ ...p, responsavelId: v || '' }))}
                                    options={(employees || [])
                                        .filter(emp => emp.isWhitelisted !== false && emp.isWhitelisted !== 0)
                                        .map(emp => ({ value: emp.id, label: emp.name }))}
                                    placeholder="Sem responsavel"
                                    isClearable
                                    bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.50"}
                                    borderColor="borderPrimary"
                                    _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="10px" color="textSecondary" textTransform="uppercase" letterSpacing="1px">
                                    Cor
                                </FormLabel>
                                <Box bg={colorMode === 'dark' ? "whiteAlpha.30" : "blackAlpha.30"} border="1px solid" borderColor={formRenomearAba.cor} borderRadius="lg" p={3}>
                                    <HStack spacing={2} mb={3}>
                                        <Box w="16px" h="16px" borderRadius="full" bg={formRenomearAba.cor} />
                                        <Text fontSize="xs" color="textPrimary" fontWeight="700">{formRenomearAba.cor}</Text>
                                    </HStack>
                                    <ColorPicker value={formRenomearAba.cor} onChange={cor => setFormRenomearAba(p => ({ ...p, cor }))} />
                                </Box>
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" gap={3}>
                        <Button size="sm" variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }} onClick={onRenomearAbaClose}>
                            Cancelar
                        </Button>
                        <Button size="sm" bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={handleSalvarRenomearAba}>
                            Salvar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </VStack>
    );
};

// --? ColunaKanban ------------------------------------------------------------?

const ColunaKanban = ({
    coluna, cards, isFirst, isLast,
    largura, altura, onResizeLargura, onResetLargura,
    onNovoCard, onVisualizarCard, onEditarCard, onDeletarCard, onMoverCard,
    onCancelarCard, onReativarCard,
    onEditarColuna, onDeletarColuna,
    isDragOver, onDragStartCard, onDragEndCard, onDragOverColuna, onDropColuna,
    currentEmployee,
}) => {
    const total = cards.filter(c => !c.canceled).reduce((acc, c) => acc + (Number(c.valor) || 0), 0);
    const [quickAddOpen, setQuickAddOpen] = useState(false);
    const [quickTitulo, setQuickTitulo] = useState('');
    const { addPipelineCard, pipelineCards } = useComercial();
    const { colorMode } = useColorMode();

    // Em vez de criar um negócio incompleto (só título), abre o formulário completo
    // já com o título digitado, para preencher os campos obrigatórios.
    const handleQuickAdd = () => {
        const titulo = quickTitulo.trim();
        setQuickTitulo('');
        setQuickAddOpen(false);
        onNovoCard(titulo);
    };

    const larguraPx = `${largura || COL_W_PADRAO}px`;

    return (
        <Box
            w={larguraPx}
            minW={larguraPx}
            position="relative"
            bg={isDragOver ? (colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100') : 'columnBg'}
            border="1px solid"
            borderColor={isDragOver ? coluna.cor : 'borderPrimary'}
            borderRadius="xl"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            {...(altura ? { h: `${altura}px` } : { maxH: COL_ALTURA_AUTO })}
            transition="background 0.15s, border-color 0.15s"
            onDragOver={(e) => onDragOverColuna?.(e, coluna.id)}
            onDrop={(e) => onDropColuna?.(e, coluna.id)}
            boxShadow={isDragOver ? `0 0 0 2px ${coluna.cor}40, 0 0 20px ${coluna.cor}30` : 'none'}
        >
            {/* Alça de largura: arraste p/ esquerda/direita; duplo clique = padrão */}
            <Box
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                w="7px"
                cursor="col-resize"
                zIndex={3}
                borderRadius="0 12px 12px 0"
                _hover={{ bg: 'secondarySubtle', boxShadow: `inset -2px 0 0 ${coluna.cor}` }}
                onPointerDown={onResizeLargura}
                onDoubleClick={onResetLargura}
                title="Arraste para ajustar a largura das colunas (duplo clique restaura)"
                style={{ touchAction: 'none' }}
            />
            {/* Header */}
            <Box
                px={3} py={3}
                borderBottom="2px solid"
                borderColor={coluna.cor}
                bg={colorMode === 'dark' ? "whiteAlpha.30" : "blackAlpha.50"}
            >
                <Flex align="center" justify="space-between">
                    <HStack spacing={2} flex={1} minW={0}>
                        <Box w="10px" h="10px" borderRadius="full" bg={coluna.cor} flexShrink={0} />
                        <Text
                            fontWeight="800"
                            fontSize="xs"
                            color="textPrimary"
                            textTransform="uppercase"
                            letterSpacing="1.5px"
                            isTruncated
                        >
                            {coluna.label}
                        </Text>
                        <Badge
                            bg={`${coluna.cor}20`}
                            color={coluna.cor}
                            fontWeight="900"
                            fontSize="10px"
                            borderRadius="full"
                            px={2}
                            flexShrink={0}
                        >
                            {cards.length}
                        </Badge>
                    </HStack>

                    <Menu placement="bottom-end">
                        <MenuButton
                            as={IconButton}
                            icon={<AiOutlineEllipsis />}
                            size="xs"
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ color: colorMode === 'dark' ? 'white' : 'black', bg: colorMode === 'dark' ? 'whiteAlpha.200' : 'blackAlpha.200' }}
                            minW="22px"
                            h="22px"
                        />
                        <MenuList bg="surface" border="1px solid" borderColor="borderPrimary" minW="160px" py={1}>
                            <MenuItem
                                bg="transparent"
                                color="textPrimary"
                                fontSize="sm"
                                fontWeight="600"
                                _hover={{ bg: colorMode === 'dark' ? 'gray.700' : 'gray.100', color: colorMode === 'dark' ? 'white' : 'black' }}
                                icon={<Icon as={AiOutlineEdit} />}
                                onClick={() => onEditarColuna(coluna)}
                            >
                                Editar coluna
                            </MenuItem>
                            <MenuItem
                                bg="transparent"
                                color="textPrimary"
                                fontSize="sm"
                                fontWeight="600"
                                _hover={{ bg: colorMode === 'dark' ? 'gray.700' : 'gray.100', color: colorMode === 'dark' ? 'white' : 'black' }}
                                icon={<Icon as={AiOutlinePlus} />}
                                onClick={onNovoCard}
                            >
                                Novo negócio aqui
                            </MenuItem>
                            <Divider borderColor="borderPrimary" my={1} />
                            <MenuItem
                                bg="transparent"
                                color="red.400"
                                fontSize="sm"
                                fontWeight="600"
                                _hover={{ bg: colorMode === 'dark' ? 'rgba(229,62,62,0.1)' : 'red.50', color: 'red.300' }}
                                icon={<Icon as={AiOutlineDelete} color="red.400" />}
                                onClick={() => onDeletarColuna(coluna.id)}
                            >
                                Excluir coluna
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </Flex>
                <Text fontSize="10px" color="textSecondary" fontWeight="700" mt={1.5}>
                    {total > 0 ? fmt(total) : '—'}
                </Text>
            </Box>

            {/* Cards • área scrollável que ocupa o espaço restante da coluna */}
            <VStack
                align="stretch"
                spacing={2}
                p={2}
                flex={1}
                minH="80px"
                overflowY="auto"
                overflowX="hidden"
                sx={{
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { bg: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bg: colorMode === 'dark' ? 'gray.700' : 'gray.400', borderRadius: 'full' },
                    '&::-webkit-scrollbar-thumb:hover': { bg: colorMode === 'dark' ? 'gray.600' : 'gray.500' },
                }}
            >
                {cards.length === 0 && !quickAddOpen && (
                    <Text fontSize="xs" color="textSecondary" textAlign="center" py={6}>
                        Nenhum negócio aqui
                    </Text>
                )}
                {cards.map((card) => (
                    <CardNegocio
                        key={card.id}
                        card={card}
                        coluna={coluna}
                        isFirst={isFirst}
                        isLast={isLast}
                        onVisualizar={onVisualizarCard}
                        onEditar={onEditarCard}
                        onDeletar={onDeletarCard}
                        onMover={onMoverCard}
                        onCancelar={onCancelarCard}
                        onReativar={onReativarCard}
                        onDragStart={onDragStartCard}
                        onDragEnd={onDragEndCard}
                    />
                ))}

                {/* Quick add inline */}
                {quickAddOpen && (
                    <Box
                        bg="cardBg"
                        border="1px solid"
                        borderColor="secondary"
                        borderRadius="lg"
                        p={2}
                    >
                        <Input
                            value={quickTitulo}
                            onChange={e => setQuickTitulo(e.target.value)}
                            placeholder="Título do negócio..."
                            size="sm"
                            bg="transparent"
                            border="none"
                            _focus={{ boxShadow: 'none' }}
                            color="textPrimary"
                            fontWeight="700"
                            autoFocus
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleQuickAdd();
                                if (e.key === 'Escape') setQuickAddOpen(false);
                            }}
                        />
                        <HStack spacing={1} mt={1} justify="flex-end">
                            <IconButton
                                icon={<AiOutlineClose />}
                                size="xs"
                                variant="ghost"
                                color="textSecondary"
                                onClick={() => setQuickAddOpen(false)}
                            />
                            <IconButton
                                icon={<AiOutlineCheck />}
                                size="xs"
                                variant="ghost"
                                color="secondary"
                                onClick={handleQuickAdd}
                            />
                        </HStack>
                    </Box>
                )}
            </VStack>

            {/* Footer */}
            <Box px={2} py={2} borderTop="1px solid" borderColor="borderPrimary">
                <Button
                    size="xs"
                    variant="ghost"
                    color="textSecondary"
                    _hover={{ color: 'secondary', bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }}
                    leftIcon={<AiOutlinePlus />}
                    w="full"
                    justifyContent="flex-start"
                    fontWeight="700"
                    onClick={() => setQuickAddOpen(true)}
                >
                    Adicionar cartão
                </Button>
            </Box>
        </Box>
    );
};

// --? CardNegocio --------------------------------------------------------------

const CardNegocio = ({ card, coluna, isFirst, isLast, onVisualizar, onEditar, onDeletar, onMover, onCancelar, onReativar, onDragStart, onDragEnd }) => {
    const [hover, setHover] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const { colorMode } = useColorMode();
    const diasAte = card.dataFechamento
        ? Math.ceil((new Date(card.dataFechamento) - new Date()) / (1000 * 60 * 60 * 24))
        : null;
    const atrasado = diasAte !== null && diasAte < 0;

    const prioridadeMeta = PRIORIDADES.find(p => p.id === card.prioridade) || PRIORIDADES[1];

    // Origem do cliente (ERP / CRM / Novo)
    const origemMeta = card.marcaId
        ? { label: 'ERP', cor: '#4299E1' }
        : card.clienteNovo
            ? { label: 'Novo', cor: '#ED8936' }
            : null;

    const handleDragStartLocal = (e) => {
        if (card.canceled) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', card.id);
        setIsDragging(true);
        onDragStart?.(card.id);
    };

    const handleDragEndLocal = () => {
        setIsDragging(false);
        onDragEnd?.();
    };

    const animacaoCard = card.color_card_date === 3
        ? `${cardPulseRed} 3s infinite ease-in-out`
        : card.color_card_date === 2
            ? `${cardPulseYellow} 3s infinite ease-in-out`
            : 'none';

    return (
        <Box
            bg="cardBg"
            border="1px solid"
            borderColor={isDragging ? coluna.cor : (card.color_card_date === 3 ? 'rgba(252, 129, 129, 0.5)' : (card.color_card_date === 2 ? 'rgba(236, 201, 75, 0.5)' : 'borderPrimary'))}
            borderRadius="lg"
            overflow="hidden"
            _hover={{
                borderColor: card.color_card_date === 3 ? '#FC8181' : (card.color_card_date === 2 ? '#ECC94B' : coluna.cor),
                boxShadow: card.color_card_date === 3
                    ? `0 0 12px rgba(252, 129, 129, 0.45)`
                    : (card.color_card_date === 2
                        ? `0 0 12px rgba(236, 201, 75, 0.45)`
                        : `0 0 12px ${coluna.cor}25`),
                transform: 'translateY(-1.5px)'
            }}
            boxShadow={
                card.color_card_date === 3
                    ? '0 0 6px rgba(252, 129, 129, 0.15), inset 0 0 4px rgba(252, 129, 129, 0.05)'
                    : (card.color_card_date === 2
                        ? '0 0 6px rgba(236, 201, 75, 0.15), inset 0 0 4px rgba(236, 201, 75, 0.05)'
                        : 'none')
            }
            animation={animacaoCard}
            transition="all 0.15s"
            cursor={isDragging ? 'grabbing' : 'pointer'}
            onClick={() => onVisualizar ? onVisualizar(card) : onEditar(card, card.colunaId)}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            position="relative"
            draggable={!card.canceled}
            onDragStart={handleDragStartLocal}
            onDragEnd={handleDragEndLocal}
            opacity={isDragging ? 0.4 : (card.canceled ? 0.55 : 1)}
            flexShrink={0}
        >
            {/* Barra lateral de prioridade */}
            <Box
                position="absolute"
                left={0} top={0} bottom={0}
                w="3px"
                bg={prioridadeMeta.cor}
            />

            <Box p={2.5} pl={3}>
                {/* Linha 1: título + responsável + badge de origem */}
                <HStack spacing={1.5} mb={1.5} align="center" justify="space-between">
                    <Text
                        fontWeight="800"
                        fontSize="xs"
                        color="textPrimary"
                        noOfLines={2}
                        lineHeight="1.3"
                        flex={1}
                        title={card.titulo}
                        textDecoration={card.canceled ? 'line-through' : 'none'}
                    >
                        {card.titulo}
                    </Text>
                    {card.canceled && (
                        <Badge
                            fontSize="8px"
                            bg="rgba(252,129,129,0.18)"
                            color="#FC8181"
                            fontWeight="900"
                            px={1.5}
                            py={0}
                            borderRadius="sm"
                            textTransform="uppercase"
                            flexShrink={0}
                        >
                            Cancelado
                        </Badge>
                    )}
                    {card.responsavel && (
                        <HStack spacing={1.5} flexShrink={0} bg={colorMode === 'dark' ? "whiteAlpha.100" : "blackAlpha.100"} px={2} py={0.5} borderRadius="full" border="1px solid" borderColor={colorMode === 'dark' ? "whiteAlpha.200" : "blackAlpha.200"}>
                            <Avatar
                                size="xxs"
                                h="16px"
                                w="16px"
                                name={card.responsavel}
                                bg="secondary"
                                color="primary"
                                fontSize="9px"
                                fontWeight="900"
                            />
                            <Text fontSize="10px" color="textPrimary" fontWeight="900" isTruncated maxW="95px">
                                {card.responsavel}
                            </Text>
                        </HStack>
                    )}
                    {origemMeta && (
                        <Badge
                            fontSize="8px"
                            bg={`${origemMeta.cor}20`}
                            color={origemMeta.cor}
                            fontWeight="900"
                            px={1.5}
                            py={0}
                            borderRadius="sm"
                            textTransform="uppercase"
                            flexShrink={0}
                        >
                            {origemMeta.label}
                        </Badge>
                    )}
                </HStack>

                {/* Linha 2: empresa (com aviso quando NÃO vem do sistema/ERP) */}
                {card.empresa && (
                    <Flex justify="space-between" align="center" mb={2} gap={2}>
                        <HStack spacing={1} minW={0} flex={1}>
                            <Icon
                                as={card.empresaExterna ? AiOutlineWarning : AiOutlineBank}
                                color={card.empresaExterna ? '#ED8936' : 'textSecondary'}
                                boxSize="10px"
                                flexShrink={0}
                            />
                            <Text
                                fontSize="10px"
                                color={card.empresaExterna ? '#ED8936' : 'textSecondary'}
                                isTruncated
                                title={card.empresaExterna ? `${card.empresa} • empresa fora do sistema (não cadastrada no ERP)` : card.empresa}
                            >
                                {card.empresa}
                            </Text>
                            {card.empresaExterna && (
                                <Tooltip label="Empresa não cadastrada no sistema/ERP" fontSize="xs" hasArrow>
                                    <Badge
                                        fontSize="7px"
                                        bg="#ED893620"
                                        color="#ED8936"
                                        fontWeight="900"
                                        px={1}
                                        py={0}
                                        borderRadius="sm"
                                        textTransform="uppercase"
                                        flexShrink={0}
                                    >
                                        Externa
                                    </Badge>
                                </Tooltip>
                            )}
                        </HStack>
                    </Flex>
                )}

                {/* Linha 3: valor + probabilidade + data + compromissos */}
                <HStack spacing={2} justify="space-between" mb={1.5}>
                    <HStack spacing={1.5}>
                        <Text fontWeight="900" fontSize="xs" color="secondary">
                            {card.valor ? fmt(card.valor) : '—'}
                        </Text>
                        {(card.compromissos || []).filter(c => !c.concluido).length > 0 && (
                            <Tooltip label={`${(card.compromissos || []).filter(c => !c.concluido).length} compromisso(s) pendente(s)`} fontSize="xs">
                                <HStack spacing={0.5} bg={colorMode === 'dark' ? "whiteAlpha.100" : "blackAlpha.100"} px={1} py={0} borderRadius="sm">
                                    <Icon as={AiOutlineCalendar} color="secondary" boxSize="9px" />
                                    <Text fontSize="9px" color="secondary" fontWeight="900">
                                        {(card.compromissos || []).filter(c => !c.concluido).length}
                                    </Text>
                                </HStack>
                            </Tooltip>
                        )}
                    </HStack>
                    <HStack spacing={1.5} flexShrink={0}>
                        {card.dataFechamento && (() => {
                            const corDataTexto = card.color_card_date === 3 ? '#FC8181' : (card.color_card_date === 2 ? '#ECC94B' : (colorMode === 'dark' ? 'gray.400' : 'gray.600'));
                            const bgBadge = card.color_card_date === 3 ? 'rgba(252, 129, 129, 0.15)' : (card.color_card_date === 2 ? 'rgba(236, 201, 75, 0.15)' : (colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100'));
                            const borderColorBadge = card.color_card_date === 3 ? 'rgba(252, 129, 129, 0.3)' : (card.color_card_date === 2 ? 'rgba(236, 201, 75, 0.3)' : (colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100'));

                            const animacaoPulsar = card.color_card_date === 3
                                ? `${pulseRed} 2s infinite ease-in-out`
                                : card.color_card_date === 2
                                    ? `${pulseYellow} 2s infinite ease-in-out`
                                    : 'none';

                            const labelTooltip = atrasado
                                ? `Atrasado por ${Math.abs(diasAte)} dia(s)`
                                : `${diasAte} dia(s) restante(s) para fechamento`;

                            return (
                                <Tooltip label={labelTooltip} fontSize="xs" hasArrow bg={colorMode === 'dark' ? 'gray.700' : 'gray.100'} color={colorMode === 'dark' ? 'white' : 'black'}>
                                    <HStack
                                        spacing={1.5}
                                        align="center"
                                        bg={bgBadge}
                                        border="1px solid"
                                        borderColor={borderColorBadge}
                                        px={2}
                                        py={0.5}
                                        borderRadius="full"
                                        transition="all 0.2s"
                                        _hover={{ filter: 'brightness(1.15)' }}
                                    >
                                        {card.color_card_date > 1 && (
                                            <Box
                                                w="6px"
                                                h="6px"
                                                borderRadius="full"
                                                bg={corDataTexto}
                                                animation={animacaoPulsar}
                                                flexShrink={0}
                                                boxShadow={`0 0 6px ${corDataTexto}`}
                                            />
                                        )}
                                        <Icon as={AiOutlineCalendar} color={corDataTexto} boxSize="10px" />
                                        <Text fontSize="9px" color={corDataTexto} fontWeight="900">
                                            {new Date(card.dataFechamento + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                        </Text>
                                    </HStack>
                                </Tooltip>
                            );
                        })()}
                        <Badge
                            bg={`${coluna.cor}20`}
                            color={coluna.cor}
                            fontSize="9px"
                            fontWeight="900"
                            borderRadius="sm"
                            px={1}
                            py={0}
                        >
                            {card.probabilidade || 0}%
                        </Badge>
                    </HStack>
                </HStack>

                {/* Barra de progresso de probabilidade */}
                <Box h="2px" bg={colorMode === 'dark' ? "gray.800" : "gray.200"} borderRadius="full" overflow="hidden">
                    <Box
                        w={`${card.probabilidade || 0}%`}
                        h="full"
                        bg={coluna.cor}
                        transition="width 0.3s"
                    />
                </Box>

                {/* Barra de ações: sempre visível, compacta */}
                <Flex
                    mt={1.5}
                    pt={1}
                    borderTop="1px solid"
                    borderColor="borderPrimary"
                    onClick={e => e.stopPropagation()}
                    align="center"
                    gap={0}
                >
                    <Tooltip label="Etapa anterior" fontSize="xs" placement="top" openDelay={400}>
                        <IconButton
                            aria-label="Anterior"
                            icon={<AiOutlineLeft />}
                            size="xs"
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ color: colorMode === 'dark' ? 'white' : 'black', bg: colorMode === 'dark' ? 'whiteAlpha.100' : 'blackAlpha.100' }}
                            isDisabled={isFirst || !!card.canceled}
                            onClick={() => onMover(card.id, 'prev')}
                            minW="20px" h="20px"
                            fontSize="9px"
                        />
                    </Tooltip>
                    <Tooltip label="Próxima etapa" fontSize="xs" placement="top" openDelay={400}>
                        <IconButton
                            aria-label="Próxima"
                            icon={<AiOutlineRight />}
                            size="xs"
                            variant="ghost"
                            color={coluna.cor}
                            _hover={{ color: colorMode === 'dark' ? 'white' : 'black', bg: `${coluna.cor}20` }}
                            isDisabled={isLast || !!card.canceled}
                            onClick={() => onMover(card.id, 'next')}
                            minW="20px" h="20px"
                            fontSize="9px"
                        />
                    </Tooltip>
                    <Spacer />
                    {card.canal && hover && (
                        <Badge
                            bg={colorMode === 'dark' ? "whiteAlpha.50" : "blackAlpha.100"}
                            color="textSecondary"
                            fontSize="8px"
                            fontWeight="700"
                            px={1}
                            py={0}
                            mr={1}
                            borderRadius="sm"
                            textTransform="uppercase"
                        >
                            {card.canal}
                        </Badge>
                    )}
                    {card.canceled ? (
                        <Tooltip label="Reativar negócio" fontSize="xs" placement="top" openDelay={400}>
                            <IconButton
                                aria-label="Reativar"
                                icon={<AiOutlineUndo />}
                                size="xs"
                                variant="ghost"
                                color="textSecondary"
                                _hover={{ color: '#ECC94B', bg: 'rgba(236,201,75,0.12)' }}
                                onClick={() => onReativar && onReativar(card)}
                                minW="20px" h="20px"
                                fontSize="9px"
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip label="Cancelar negócio" fontSize="xs" placement="top" openDelay={400}>
                            <IconButton
                                aria-label="Cancelar"
                                icon={<AiOutlineCloseCircle />}
                                size="xs"
                                variant="ghost"
                                color="textSecondary"
                                _hover={{ color: '#FC8181', bg: 'rgba(252,129,129,0.12)' }}
                                onClick={() => onCancelar && onCancelar(card)}
                                minW="20px" h="20px"
                                fontSize="9px"
                            />
                        </Tooltip>
                    )}
                    <Tooltip label="Excluir" fontSize="xs" placement="top" openDelay={400}>
                        <IconButton
                            aria-label="Excluir"
                            icon={<AiOutlineDelete />}
                            size="xs"
                            variant="ghost"
                            color="textSecondary"
                            _hover={{ color: 'red.400', bg: 'rgba(229,62,62,0.1)' }}
                            onClick={() => onDeletar(card)}
                            minW="20px" h="20px"
                            fontSize="9px"
                        />
                    </Tooltip>
                </Flex>
            </Box>
        </Box>
    );
};

export default Pipeline;
